// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2024-2026 Pau Aliagas <linuxnow@gmail.com>
/**
 * PWA Player with RendererLite
 *
 * Lightweight PWA player using modular PlayerCore orchestration.
 * Platform layer handles UI, DOM manipulation, and platform-specific features.
 */

import { RendererLite } from '@xiboplayer/renderer';
import { StoreClient, DownloadManager, BARRIER } from '@xiboplayer/cache';
import { PlayerCore, CORE_EVENTS as E } from '@xiboplayer/core';
import { parseLayoutDuration, parseLayoutFile } from '@xiboplayer/schedule';
import { createLogger, registerLogSink, PLAYER_API } from '@xiboplayer/utils';
import { DatasourceClient, attachHostBridge } from '@xiboplayer/datasource';
import { DownloadOverlay, getDefaultOverlayConfig } from './download-overlay.js';
import { TimelineOverlay, isTimelineVisible } from './timeline-overlay.js';
import { SetupOverlay } from './setup-overlay.js';

declare const __APP_VERSION__: string;
declare const __BUILD_DATE__: string;

const log = createLogger('PWA');

// ContentStore key prefix — mirrors PLAYER_API without leading slash
const STORE_PREFIX = PLAYER_API.slice(1);

// Dynamic base path — same build serves /player/pwa/, /player/pwa-xmds/, /player/pwa-xlr/
const PLAYER_BASE = new URL('./', window.location.href).pathname.replace(/\/$/, '');

// Import core modules (will be loaded at runtime)
let cacheWidgetHtml: any;
let scheduleManager: any;
let config: any;
let RestClient: any;
let XmdsClient: any;
let ProtocolDetector: any;
let XmrWrapper: any;
let store: StoreClient;
let downloadManager: DownloadManager;
let StatsCollector: any;
let formatStats: any;
let LogReporter: any;
let formatLogs: any;
let DisplaySettings: any;
let SyncManager: any;
let computeStagger: any;

// SDK package versions (populated in loadCoreModules)
const sdkVersions: Record<string, string> = {};

class PwaPlayer {
  private renderer!: RendererLite;
  private core!: PlayerCore;
  private xmds!: any;
  private downloadOverlay: DownloadOverlay | null = null;
  private timelineOverlay: TimelineOverlay | null = null;
  private setupOverlay: SetupOverlay | null = null;
  private statsCollector: any = null;
  private logReporter: any = null;
  private displaySettings: any = null;
  private currentScheduleId: number = -1; // Track scheduleId for stats
  private scheduledLayoutIds: Set<number> = new Set(); // Layout IDs from current schedule
  private preparingLayoutId: number | null = null; // Guard against concurrent prepareLayout calls
  private _pendingRetryLayoutId: number | null = null; // Queued retry when check-pending-layout arrives during preparation
  private _screenshotInterval: any = null;
  private _screenshotMethod: 'electron' | 'displayMedia' | 'html2canvas' | null = null;
  private _html2canvasMod: any = null;
  private _screenshotInFlight = false; // Concurrency guard — one capture at a time
  private _wakeLock: any = null; // Screen Wake Lock sentinel
  private syncManager: any = null; // Multi-display sync coordinator
  private _syncGroupDebounceTimer: ReturnType<typeof setTimeout> | null = null; // #236 tag→group bridge
  private _pendingSyncGroup: string | null = null; // Latest group observed on a layout while debouncing
  private _currentLayoutEnableStat: boolean = true; // enableStat from current layout XLF
  private _probeTimer: any = null; // Debounce timer for duration probing
  private _mediaStatusTimer: ReturnType<typeof setTimeout> | null = null; // Debounce timer for media status check
  private _pendingFollowerStats: any[] | null = null; // In-flight stats delegated to lead
  private _pendingFollowerLogs: any[] | null = null; // In-flight logs delegated to lead
  private _iframeObserver: MutationObserver | null = null; // Iframe key-forwarding observer
  private _swIcHandler: any = null; // SW Interactive Control message handler
  private _chunkConfig: any = null; // Device-adaptive chunk configuration
  private _fileIdToSaveAs: Map<string, string> = new Map(); // Numeric file ID → storedAs filename
  private _cachedMediaKeys: Set<string> = new Set(); // saveAs keys confirmed cached (avoids HEAD 404s)
  private protocolDetector: any = null; // CMS protocol auto-detector
  private datasourceClient: DatasourceClient | null = null; // Shared xp:datasource cache (#235 G9)
  private _datasourceBridge: { destroy(): void; stats(): { subscribers: number } } | null = null;

  async init() {
    log.info('Initializing player with RendererLite + PlayerCore...');

    // Load core modules
    await this.loadCoreModules();

    // Register Service Worker for offline-first kiosk mode
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register(`${PLAYER_BASE}/sw-pwa.js?v=${Date.now()}`, {
          scope: `${PLAYER_BASE}/`,
          type: 'module',
          updateViaCache: 'none'
        });
        log.info('Service Worker registered for offline mode:', registration.scope);

        // Request persistent storage (kiosk requirement)
        if (navigator.storage && navigator.storage.persist) {
          const persistent = await navigator.storage.persist();
          if (persistent) {
            log.info('Persistent storage granted - cache won\'t be evicted');
          } else {
            log.warn('Persistent storage denied - cache may be evicted');
          }
        }
      } catch (error) {
        log.warn('Service Worker registration failed:', error);
      }
    }

    // Initialize StoreClient (REST) + DownloadManager (main thread)
    log.info('Initializing cache clients...');
    store = new StoreClient();
    const { calculateChunkConfig } = await import('@xiboplayer/sw');
    this._chunkConfig = calculateChunkConfig(log);
    downloadManager = new DownloadManager({
      concurrency: this._chunkConfig.concurrency,
      chunkSize: this._chunkConfig.chunkSize,
      chunksPerFile: 2,
    });
    log.info('Cache clients ready — StoreClient + DownloadManager');

    // Create renderer
    const container = document.getElementById('player-container');
    if (!container) {
      throw new Error('No #player-container found');
    }

    this.renderer = new RendererLite(
      {
        cmsUrl: config.cmsUrl,
        hardwareKey: config.hardwareKey
      },
      container,
      {
        // Provide fileId→saveAs map for layout background resolution
        fileIdToSaveAs: this._fileIdToSaveAs,

        // Provide widget HTML resolver — check ContentStore via proxy
        getWidgetHtml: async (widget: any) => {
          const widgetPath = `${PLAYER_API}/widgets/${widget.layoutId}/${widget.regionId}/${widget.id}`;
          log.debug(`Looking for widget HTML at: ${widgetPath}`, widget);

          try {
            const exists = await store.has(`${STORE_PREFIX}/widgets`, `${widget.layoutId}/${widget.regionId}/${widget.id}`);
            if (exists) {
              log.debug(`Widget HTML found in store, using mirror URL for iframe`);
              return { url: widgetPath, fallback: widget.raw || '' };
            } else {
              log.warn(`No widget HTML found in store: ${widgetPath}`);
            }
          } catch (error) {
            log.error(`Failed to check widget HTML for ${widget.id}:`, error);
          }

          // Fallback to widget.raw (XLF template)
          log.warn(`Using widget.raw fallback for ${widget.id}`);
          return widget.raw || '';
        }
      }
    );

    // Create PlayerCore (with CMS-namespaced offline cache DB)
    this.core = new PlayerCore({
      config,
      xmds: this.xmds,
      cache: store,
      schedule: scheduleManager,
      renderer: this.renderer,
      xmrWrapper: XmrWrapper,
      statsCollector: this.statsCollector,
      displaySettings: this.displaySettings,
      cmsId: config.activeCmsId,
    });

    // Setup platform-specific event handlers
    this.setupCoreEventHandlers();
    this.setupRendererEventHandlers();
    this.setupInteractiveControl();
    this.setupDataConnectorNotify();
    this.setupDatasourceBridge();
    this.setupRemoteControls();

    // Setup UI
    this.updateConfigDisplay();

    // Online/offline event listeners for seamless offline mode
    window.addEventListener('online', () => {
      log.info('Browser reports online — triggering immediate collection');
      this.updateStatus('Back online, syncing...');
      this.removeOfflineIndicator();
      this.core.collectNow().catch((error: any) => {
        log.error('Failed to collect after coming online:', error);
      });
    });
    window.addEventListener('offline', () => {
      log.warn('Browser reports offline — continuing playback with cached data');
      this.updateStatus('Offline mode — using cached content');
      this.showOfflineIndicator();
    });

    // Initialize download progress overlay (configurable debug feature)
    // Respect controls.keyboard.debugOverlays — if disabled, don't restore overlays
    const controls = this.getControls();
    const debugOverlaysEnabled = (controls.keyboard || {}).debugOverlays === true;

    const overlayConfig = getDefaultOverlayConfig();
    if (overlayConfig.enabled && debugOverlaysEnabled) {
      this.downloadOverlay = new DownloadOverlay(overlayConfig);
      this.downloadOverlay.setProgressCallback(() => downloadManager.getProgress());
      log.info('Download overlay enabled (hover bottom-right corner)');
    }

    // Timeline overlay — created on first T key press (or if previously visible)
    if (isTimelineVisible() && debugOverlaysEnabled) {
      this.timelineOverlay = new TimelineOverlay(true, (layoutId) => this.skipToLayout(layoutId));
    }

    // Listen for certificate warnings from Electron main process
    this.setupCertWarnings();

    // Listen for XMR connection status changes
    this.setupXmrWarning();

    // Request Screen Wake Lock to prevent display sleep
    await this.requestWakeLock();

    // Re-acquire wake lock when tab becomes visible again
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.requestWakeLock();
      }
    });

    // Start collection cycle
    await this.core.collect();

    log.info('Player initialized successfully');
  }

  /**
   * Request Screen Wake Lock to prevent display from sleeping
   * Re-acquired on visibility change (browser releases it when tab is hidden)
   */
  private async requestWakeLock() {
    if (!('wakeLock' in navigator)) {
      log.debug('Wake Lock API not supported');
      return;
    }

    try {
      this._wakeLock = await (navigator as any).wakeLock.request('screen');
      log.info('Screen Wake Lock acquired — display will stay on');

      this._wakeLock.addEventListener('release', () => {
        log.debug('Screen Wake Lock released');
        this._wakeLock = null;
      });
    } catch (error: any) {
      log.warn('Wake Lock request failed:', error?.message);
    }
  }

  /**
   * Listen for certificate warnings from Electron and show in the top bar.
   * The #overlay bar (defined in index.html) is the status bar with
   * #config-info (left) and #status (right). If it was removed (statusBarOnHover
   * not set), we recreate it. Cert warnings make the bar always visible.
   */
  private setupCertWarnings() {
    const warnedHosts = new Set<string>();

    window.addEventListener('cert-warning', ((e: CustomEvent) => {
      const { host, error } = e.detail;
      if (warnedHosts.has(host)) return;
      warnedHosts.add(host);

      log.warn(`Invalid SSL certificate accepted for stream: ${host} (${error})`);

      // Find or recreate the top bar
      let overlay = document.getElementById('overlay');
      let created = false;
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'overlay';
        // Recreate child structure: config-info | status
        const info = document.createElement('div');
        info.id = 'config-info';
        overlay.appendChild(info);
        const status = document.createElement('div');
        status.id = 'status';
        overlay.appendChild(status);
        document.body.appendChild(overlay);
        created = true;
      }

      // Find or create the cert warning span between #config-info and #status
      let certSpan = document.getElementById('cert-warnings');
      if (!certSpan) {
        certSpan = document.createElement('span');
        certSpan.id = 'cert-warnings';
        certSpan.style.cssText = 'color: #ffaa33; flex: 0 0 auto;';
        const statusEl = document.getElementById('status');
        overlay.insertBefore(certSpan, statusEl);
      }

      const hosts = [...warnedHosts].join(', ');
      certSpan.textContent = `\u26A0 SSL: ${hosts}`;

      // Don't force always-visible — let hover-only CSS handle show/hide

      // If we recreated the overlay, repopulate config info
      if (created) this.updateConfigDisplay();
    }) as EventListener);
  }

  /**
   * Show/hide an XMR disconnected warning in the top bar.
   * Placed before #cert-warnings (or before #status if no cert warnings).
   */
  private setupXmrWarning() {
    this.core.on('xmr-status', ({ connected }: { connected: boolean }) => {
      const overlay = document.getElementById('overlay');
      if (!overlay) return;

      let span = document.getElementById('xmr-warning');

      if (!connected) {
        if (!span) {
          span = document.createElement('span');
          span.id = 'xmr-warning';
          span.style.cssText = 'color: #ff6666; flex: 0 0 auto;';
          // Insert before cert-warnings or status (whichever comes first)
          const anchor = document.getElementById('cert-warnings') || document.getElementById('status');
          overlay.insertBefore(span, anchor);
        }
        span.textContent = '\u26A0 XMR disconnected';
      } else {
        span?.remove();
      }
    });
  }

  /**
   * Load core modules
   */
  private async loadCoreModules() {
    try {
      const [
        cacheModule, xmdsModule, scheduleModule, configModule,
        xmrModule, statsModule, displaySettingsModule, coreModule,
        rendererModule, syncModule,
      ] = await Promise.all([
        import('@xiboplayer/cache'),
        import('@xiboplayer/xmds'),
        import('@xiboplayer/schedule'),
        import('@xiboplayer/utils'),
        import('@xiboplayer/xmr'),
        import('@xiboplayer/stats'),
        import('@xiboplayer/settings'),
        import('@xiboplayer/core'),
        import('@xiboplayer/renderer'),
        import('@xiboplayer/sync'),
      ]);

      cacheWidgetHtml = cacheModule.cacheWidgetHtml;
      SyncManager = syncModule.SyncManager;
      computeStagger = syncModule.computeStagger;
      scheduleManager = scheduleModule.scheduleManager;
      config = configModule.config;
      RestClient = xmdsModule.RestClient;
      XmdsClient = xmdsModule.XmdsClient;
      ProtocolDetector = xmdsModule.ProtocolDetector;
      XmrWrapper = xmrModule.XmrWrapper;
      StatsCollector = statsModule.StatsCollector;
      formatStats = statsModule.formatStats;
      LogReporter = statsModule.LogReporter;
      formatLogs = statsModule.formatLogs;
      DisplaySettings = displaySettingsModule.DisplaySettings;

      // Capture SDK package versions
      sdkVersions.core = coreModule.VERSION || '?';
      sdkVersions.cache = cacheModule.VERSION || '?';
      sdkVersions.renderer = rendererModule.VERSION || '?';
      sdkVersions.schedule = scheduleModule.VERSION || '?';
      sdkVersions.xmds = xmdsModule.VERSION || '?';
      sdkVersions.xmr = xmrModule.VERSION || '?';
      sdkVersions.utils = configModule.VERSION || '?';
      sdkVersions.stats = statsModule.VERSION || '?';
      sdkVersions.settings = displaySettingsModule.VERSION || '?';

      // Get MAC address from Electron if available (for WOL support)
      if ((window as any).electronAPI?.getSystemInfo) {
        try {
          const sysInfo = await (window as any).electronAPI.getSystemInfo();
          if (sysInfo.macAddress) {
            config.macAddress = sysInfo.macAddress;
          }
        } catch (_) { /* pure PWA — no Electron API */ }
      }

      // Transport selection:
      //   transport: "rest"   → forced REST API
      //   transport: "xmds"   → forced SOAP
      //   transport: "auto"   → probe REST → SOAP fallback (default)
      //   /player/pwa-xmds/   → forced SOAP (URL-based override)
      //   ?transport=xmds     → forced SOAP (query param override)
      const cfgTransport = config.transport !== 'auto' ? config.transport : undefined;
      const urlTransport = new URLSearchParams(window.location.search).get('transport');
      const transport = urlTransport
        || (PLAYER_BASE.includes('pwa-xmds') ? 'xmds' : null)
        || cfgTransport
        || 'auto';

      // Use ProtocolDetector for auto-detection with re-probe support.
      // The detector's first probe uses a generous timeout (10s default)
      // to tolerate cold CMS start-ups — an initial 5-6s TLS+PHP-FPM
      // warm-up used to trip the old 3s timeout and permanently lock
      // the player into XMDS fallback for the rest of the session.
      this.protocolDetector = new ProtocolDetector(config.cmsUrl, RestClient, XmdsClient);
      const forceProtocol = (transport === 'auto') ? undefined : transport;
      const { client, protocol } = await this.protocolDetector.detect(config, forceProtocol);
      this.xmds = client;

      // If we fell back to XMDS, start a background auto-reprobe loop.
      // When REST recovers (e.g. CMS restart, network fix), the detector
      // swaps the live client pointer here and we're transparently back
      // on REST without needing to restart the player.
      // Skipped for explicitly forced protocols — if the operator asked
      // for XMDS, stay on XMDS.
      if (protocol === 'xmds' && forceProtocol === undefined) {
        this.protocolDetector.startAutoReprobe(config, (newClient: any) => {
          this.xmds = newClient;
          log.info('[Protocol] Promoted from XMDS back to REST — live client swapped');
        });
      }

      // Initialize stats collector (namespaced by CMS ID)
      const cmsId = config.activeCmsId;
      this.statsCollector = new StatsCollector(cmsId);
      await this.statsCollector.init();
      log.info(`Stats collector initialized${cmsId ? ` (CMS: ${cmsId})` : ''}`);

      // Initialize log reporter for CMS log submission (namespaced by CMS ID)
      this.logReporter = new LogReporter(cmsId);
      await this.logReporter.init();
      log.info(`Log reporter initialized${cmsId ? ` (CMS: ${cmsId})` : ''}`);

      // Serialize log args to string (shared by log reporter and console forwarder)
      const serializeArgs = (args: any[]) => args.map((a: any) => typeof a === 'string' ? a : JSON.stringify(a)).join(' ');

      // Bridge logger output to LogReporter for CMS submission
      registerLogSink(({ level, name, args }: { level: string; name: string; args: any[] }) => {
        if (!this.logReporter) return;
        const message = serializeArgs(args);
        this.logReporter.log(level, `[${name}] ${message}`, 'PLAYER').catch(() => {});
      });

      // Forward console logs to proxy stdout (for journald/log analysis).
      // Controlled by debug.consoleLogs in config.json.
      // Optional debug.consoleLogsInterval (seconds) sets the batch flush interval (default 10s).
      const debugConfig = config.debug;
      if (debugConfig?.consoleLogs) {
        const flushIntervalMs = (debugConfig.consoleLogsInterval || 10) * 1000;
        let batch: Array<{ level: string; name: string; message: string; ts: string }> = [];
        let flushTimer: ReturnType<typeof setTimeout> | null = null;

        const flushLogs = () => {
          if (batch.length === 0) return;
          const payload = batch;
          batch = [];
          flushTimer = null;
          // Fire-and-forget POST — log forwarding must never block the player
          fetch('/debug/log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }).catch(() => {});
        };

        registerLogSink(({ level, name, args }: { level: string; name: string; args: any[] }) => {
          const message = serializeArgs(args);
          batch.push({ level, name, message, ts: new Date().toISOString() });
          if (!flushTimer) {
            flushTimer = setTimeout(flushLogs, flushIntervalMs);
          }
        });

        log.info(`Console log forwarding to proxy enabled (flush every ${flushIntervalMs / 1000}s)`);
      }

      // Initialize display settings manager
      this.displaySettings = new DisplaySettings();
      log.info('Display settings manager initialized');

      // Log version and environment information for debugging
      const buildDate = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : '?';
      const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '?';
      log.info(`v${appVersion} built ${buildDate}`);
      const versionParts = Object.entries(sdkVersions).map(([k, v]) => `${k}=${v}`).join(' ');
      log.info(`SDK: ${versionParts}`);
      const isElectron = !!(window as any).electronAPI;
      const electronVersion = isElectron ? (navigator.userAgent.match(/Electron\/([\d.]+)/)?.[1] || '?') : null;
      const chromeVersion = navigator.userAgent.match(/Chrome\/([\d.]+)/)?.[1] || '?';
      const platform = isElectron ? `Electron ${electronVersion} / Chrome ${chromeVersion}` : `Chrome ${chromeVersion}`;
      log.info(`Env: PWA v${appVersion} | ${platform} | ${navigator.platform} | ${screen.width}x${screen.height}`);

      log.info('Core modules loaded');
    } catch (error) {
      log.error('Failed to load core modules:', error);
      throw error;
    }
  }

  /**
   * Setup PlayerCore event handlers (Platform-specific UI updates)
   */
  private setupCoreEventHandlers() {
    // Delegate to focused handler groups
    this.setupSyncEventHandlers();
    this.setupDownloadEventHandlers();
    this.setupCommandEventHandlers();

    // Collection events
    this.core.on(E.COLLECTION_START, () => {
      this.updateStatus('Collecting data from CMS...');
    });

    this.core.on(E.REGISTER_COMPLETE, (regResult: any) => {
      const displayName = this.displaySettings?.getDisplayName() || regResult.displayName || config.hardwareKey;
      this.updateStatus(`Registered: ${displayName}`);

      // Update page title with display name
      if (this.displaySettings) {
        document.title = `xiboplayer - ${this.displaySettings.getDisplayName()}`;
      }

      // Set display location from CMS settings
      const lat = parseFloat(regResult?.settings?.latitude);
      const lng = parseFloat(regResult?.settings?.longitude);
      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        log.info(`Display location from CMS: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        if (scheduleManager?.setLocation) {
          scheduleManager.setLocation(lat, lng);
        }
      } else if (this.core.requestGeoLocation) {
        // No CMS coordinates — try browser Geolocation API as fallback
        log.info('No CMS coordinates, requesting browser geolocation...');
        this.core.requestGeoLocation();
      }

      // Multi-display sync: local config fallback when CMS doesn't provide syncConfig
      if (!regResult.syncConfig && config.data?.sync) {
        log.info('[Sync] Using local sync config (CMS did not provide syncConfig)');
        this.core.syncConfig = config.data.sync;
        this.core.emit(E.SYNC_CONFIG, config.data.sync);
      }
    });

    // NOTE: Two OFFLINE_MODE listeners are intentional — this one handles UI,
    // setupSyncEventHandlers() registers a second one for sync bootstrap.
    this.core.on(E.OFFLINE_MODE, (isOffline: boolean) => {
      if (isOffline) {
        this.updateStatus('Offline mode — using cached content');
        this.showOfflineIndicator();
      } else {
        this.updateStatus('Back online');
        this.removeOfflineIndicator();
      }
    });

    this.core.on(E.SCHEDULE_RECEIVED, (schedule: any) => {
      this.updateStatus('Processing schedule...');

      // Extract scheduleId for stats tracking
      // Check layouts or campaigns for scheduleId
      if (schedule.layouts && schedule.layouts.length > 0) {
        this.currentScheduleId = parseInt(schedule.layouts[0].scheduleid) || -1;
      } else if (schedule.campaigns && schedule.campaigns.length > 0) {
        this.currentScheduleId = parseInt(schedule.campaigns[0].scheduleid) || -1;
      }

      // Selectively clear preloaded layouts not in the new schedule.
      // Keep warm entries whose layout ID is still scheduled — their DOM is still valid.
      // (The CMS schedule CRC changes every collection due to timestamps, even when
      // the actual layout list hasn't changed. Blindly clearing would destroy preloads.)
      if (this.renderer?.layoutPool) {
        const scheduledIds = new Set<number>();
        if (schedule.layouts) {
          for (const l of schedule.layouts) {
            const id = parseLayoutFile(l.file || l.id || l);
            if (id) scheduledIds.add(id);
          }
        }
        if (schedule.campaigns) {
          for (const c of schedule.campaigns) {
            if (c.layouts) {
              for (const l of c.layouts) {
                const id = parseLayoutFile(l.file || l.id || l);
                if (id) scheduledIds.add(id);
              }
            }
          }
        }
        const cleared = this.renderer.layoutPool.clearWarmNotIn(scheduledIds);
        if (cleared > 0) {
          log.info(`Cleared ${cleared} preloaded layout(s) no longer in schedule`);
        }
        this.scheduledLayoutIds = scheduledIds;
      }

      log.debug('Current scheduleId for stats:', this.currentScheduleId);
    });

    this.core.on(E.LAYOUT_PREPARE_REQUEST, async (layoutId: number) => {
      await this.prepareLayout(layoutId);
      // Non-sync or no layout playing yet: show immediately.
      // Sync transitions: onLayoutShow handles showing with stagger.
      if (!this.syncManager || this.renderer.getCurrentLayoutId() === null) {
        this.renderer.showLayout(layoutId);
      }
    });

    this.core.on(E.LAYOUT_ALREADY_PLAYING, (layoutId: number) => {
      // The schedule says this layout should still be playing. Verify the renderer
      // actually has an active timer — if not, the renderer stalled (e.g. after a
      // GPU crash/recovery or restart) and we need to force a re-show.
      if (!this.renderer.hasActiveLayoutTimer()) {
        log.warn(`Layout ${layoutId} has no active timer — restarting layout`);
        this.renderer.stopCurrentLayout();
        // stopCurrentLayout → layoutEnd → advanceToNextLayout → re-prepares + shows
      }
    });

    this.core.on(E.LAYOUT_EXPIRE_CURRENT, () => {
      log.info('Schedule changed — expiring current layout');
      this.renderer.stopCurrentLayout();
      // stopCurrentLayout() emits layoutEnd → the layoutEnd handler
      // calls advanceToNextLayout() which picks the next scheduled layout
    });

    this.core.on(E.NO_LAYOUTS_SCHEDULED, () => {
      this.updateStatus('No layouts scheduled');
    });

    this.core.on(E.COLLECTION_COMPLETE, () => {
      const layoutId = this.core.getCurrentLayoutId();
      if (layoutId) {
        this.updateStatus(`Playing layout ${layoutId}`);
      } else if (this.preparingLayoutId) {
        this.updateStatus(`Downloading layout ${this.preparingLayoutId}...`);
      }

      // Duration probing is handled by the debounced re-probe (3s after last
      // file cached) — avoids 404s from probing before downloads complete.
    });

    this.core.on(E.COLLECTION_ERROR, async (error: any) => {
      this.updateStatus(`Collection error: ${error}`, 'error');

      // Display not found / not authorized — show setup screen so user can re-register
      const msg = error?.message || String(error);
      if (msg.includes('403') && (msg.includes('Display not found') || msg.includes('not authorized'))) {
        log.warn('Display not registered or not authorized — showing setup screen');
        if (!this.setupOverlay) {
          this.setupOverlay = new SetupOverlay();
        }
        this.setupOverlay.show();
        return;
      }

      // Report fault to CMS (triggers dashboard alert)
      this.reportFault('COLLECTION_FAILED', `Collection cycle failed: ${error?.message || error}`);
    });

    this.core.on(E.XMR_CONNECTED, (url: string) => {
      log.info('XMR connected:', url);
    });

    this.core.on(E.XMR_MISCONFIGURED, (info: { reason: string; url?: string; message: string }) => {
      log.warn(`XMR misconfigured (${info.reason}): ${info.message}`);
    });

    // Log level changes from CMS (overlays are controlled by config.controls, not log level)
    this.core.on(E.LOG_LEVEL_CHANGED, () => {
      log.info(`Log level changed`);
    });

    // Overlay layout push from XMR
    this.core.on(E.OVERLAY_LAYOUT_REQUEST, async (layoutId: number) => {
      log.info('Overlay layout requested:', layoutId);
      // Re-use existing overlay rendering (schedule-driven overlays already work)
      // Just need to prepare and render the overlay layout
      await this.prepareLayout(layoutId);
      this.renderer.showLayout(layoutId);
    });

    // Revert to schedule (undo XMR layout override)
    this.core.on(E.REVERT_TO_SCHEDULE, () => {
      log.info('Reverting to scheduled content');
      this.updateStatus('Reverting to schedule...');
    });

    // Display settings events
    if (this.displaySettings) {
      this.displaySettings.on('interval-changed', (newInterval: number) => {
        log.info(`Collection interval changed to ${newInterval}s`);
      });

      this.displaySettings.on('settings-applied', (_settings: any, changes: string[]) => {
        if (changes.length > 0) {
          log.info('Settings updated from CMS:', changes.join(', '));
        }
        // Start periodic screenshots once we have settings (only first time)
        if (!this._screenshotInterval) {
          this.startScreenshotInterval();
        }
      });
    }

    // Stats submission
    this.core.on(E.SUBMIT_STATS_REQUEST, async () => {
      await this.submitStats();
    });

    // Log submission to CMS
    this.core.on(E.SUBMIT_LOGS_REQUEST, async () => {
      await this.submitLogs();
    });

    // Screenshot capture (triggered by XMR or periodic interval)
    this.core.on(E.SCREENSHOT_REQUEST, async () => {
      await this.captureAndSubmitScreenshot();
    });

    // Handle check-pending-layout events — layout was pending download, now ready
    this.core.on(E.CHECK_PENDING_LAYOUT, async (layoutId: number) => {
      await this.prepareLayout(layoutId);
      this.renderer.showLayout(layoutId);
    });

    // Navigate to widget (navWidget action via triggerCode from schedule-level actions)
    this.core.on(E.NAVIGATE_TO_WIDGET, (action: any) => {
      if (action.targetId) {
        this.renderer.navigateToWidget(action.targetId);
      } else {
        log.warn('navigate-to-widget action has no targetId:', action);
      }
    });

    // Timeline overlay — visualize upcoming schedule
    this.core.on(E.TIMELINE_UPDATED, (timeline: any[]) => {
      const id = this.core.getCurrentLayoutId();
      const dur = id ? this.core.getLayoutDuration(id) : undefined;
      this.timelineOverlay?.update(timeline, id, dur);
    });
  }

  /**
   * Layout-tag → sync-group bridge (#236, Design A).
   *
   * Called from the renderer's `layoutStart` handler. Inspects the
   * newly-active layout's parsed `<tags>` for a single
   * `xp-sync-group:NAME` marker (emitted by the
   * xiboplayer-smil-tools translator). When NAME differs from the
   * current `syncManager.syncConfig.syncGroup`, schedules a
   * debounced `setSyncGroup(NAME)` call 2 s out.
   *
   * Debouncing coalesces rapid back-to-back layout changes (day-part
   * boundaries, preemption) so we tear down the transport at most
   * once per 2 s window.
   *
   * Design decision — no-op when the layout has NO `xp-sync-group:*`
   * tag:
   *   The translator only emits the tag when the source SMIL had
   *   `xp:sync-group="…"`. A plain layout without the tag is
   *   "not-grouped by author intent", not "explicitly leave the
   *   group". Forcing `setSyncGroup(null)` on every ungrouped layout
   *   would churn the cohort on each rotation. Explicit leave needs
   *   an explicit mechanism (e.g. `<tag>xp-sync-group:none</tag>`
   *   or an empty-value variant) — deferred until someone asks for
   *   it. Multiple `xp-sync-group:*` tags on one layout: take the
   *   first and log a warning.
   */
  private handleLayoutSyncGroupTag(tags: string[]) {
    if (!this.syncManager || !Array.isArray(tags) || tags.length === 0) return;

    const prefix = 'xp-sync-group:';
    const matches = tags.filter((t) => typeof t === 'string' && t.startsWith(prefix));
    if (matches.length === 0) return; // No tag → keep current group (see decision note above)

    if (matches.length > 1) {
      log.warn(`[Sync] Multiple xp-sync-group tags on layout — using first: ${matches.map((m) => m.slice(prefix.length)).join(', ')}`);
    }

    const groupName = matches[0].slice(prefix.length).trim();
    if (!groupName) return;

    const current = this.syncManager.syncConfig?.syncGroup == null
      ? null
      : String(this.syncManager.syncConfig.syncGroup);
    if (groupName === current) return; // Already in this group — fast-path no-op

    this._pendingSyncGroup = groupName;
    if (this._syncGroupDebounceTimer) {
      clearTimeout(this._syncGroupDebounceTimer);
    }

    this._syncGroupDebounceTimer = setTimeout(() => {
      this._syncGroupDebounceTimer = null;
      const target = this._pendingSyncGroup;
      this._pendingSyncGroup = null;
      if (!target || !this.syncManager) return;

      // Re-check vs current in case another handler already switched us.
      const now = this.syncManager.syncConfig?.syncGroup == null
        ? null
        : String(this.syncManager.syncConfig.syncGroup);
      if (target === now) return;

      log.info(`[Sync] Layout tag → setSyncGroup('${target}') (was '${now ?? 'none'}')`);
      try {
        this.syncManager.setSyncGroup(target);
      } catch (err) {
        log.error('[Sync] setSyncGroup failed:', err);
      }
    }, 2000);
  }

  /**
   * Setup multi-display sync event handlers.
   * Handles SYNC_CONFIG and offline sync fallback via OFFLINE_MODE.
   */
  private setupSyncEventHandlers() {
    // Offline sync: if CMS is unreachable but local config has sync settings,
    // start SyncManager so LAN-only displays can still sync with each other.
    this.core.on(E.OFFLINE_MODE, (isOffline: boolean) => {
      if (isOffline && !this.syncManager && config.data?.sync) {
        log.info('[Sync] Offline mode with local sync config — starting sync');
        this.core.syncConfig = config.data.sync;
        this.core.emit(E.SYNC_CONFIG, config.data.sync);
      }
    });

    // Multi-display sync: create SyncManager when CMS provides sync config (or local fallback)
    this.core.on(E.SYNC_CONFIG, async (syncConfig: any) => {
      if (this.syncManager) {
        this.syncManager.stop();
      }

      // Cross-device sync: build WebSocket relay URL.
      // Always rebuild for followers (mDNS re-discovers lead IP/port each cycle).
      // Lead connects to its own relay (localhost).
      if (syncConfig.syncPublisherPort) {
        if (syncConfig.syncGroupId) {
          syncConfig.syncGroup = String(syncConfig.syncGroupId);
        }

        if (syncConfig.isLead) {
          syncConfig.relayUrl = `ws://localhost:${syncConfig.syncPublisherPort}/sync`;
          // Trigger mDNS advertisement so followers can discover us
          fetch('/system/advertise-sync', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ syncGroupId: syncConfig.syncGroupId, port: syncConfig.syncPublisherPort, displayId: config.hardwareKey }),
          }).catch(() => {});
        } else {
          // Try mDNS discovery first, fall back to CMS-provided IP
          let leadHost = syncConfig.syncGroup;
          try {
            const res = await fetch(`/system/discover-lead?syncGroupId=${syncConfig.syncGroupId}`);
            if (res.ok) {
              const { host, port } = await res.json();
              leadHost = host;
              log.info(`mDNS discovered lead at ${host}:${port}`);
            }
          } catch (_) {
            log.warn('mDNS discovery failed, using CMS-provided IP');
          }
          syncConfig.relayUrl = `ws://${leadHost}:${syncConfig.syncPublisherPort}/sync`;
        }
      }

      // Persist resolved sync config to config.json so offline restarts
      // can sync over LAN without CMS. Strips runtime-only fields.
      // Merge with existing sync config to preserve local-only fields
      // (topology, choreography, staggerMs, gridCols, gridRows).
      const { syncToken, ...persistable } = syncConfig;
      const merged = { ...(config.data?.sync || {}), ...persistable };
      if ((window as any).electronAPI?.setConfig) {
        (window as any).electronAPI.setConfig({ sync: merged });
      } else {
        fetch('/config', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sync: merged }),
        }).catch(() => {});
      }

      // Pass CMS server key as sync token for relay auth (shared by all displays on this CMS)
      if (!syncConfig.syncToken) {
        syncConfig.syncToken = config.cmsKey;
      }

      this.syncManager = new SyncManager({
        displayId: config.hardwareKey,
        syncConfig,
        onLayoutChange: async (layoutId: string) => {
          // Wall mode: map lead's layout to this display's position-specific layout
          const layoutMap = syncConfig.layoutMap || config.sync?.layoutMap;
          const mappedId = layoutMap?.[layoutId] ?? layoutId;
          if (mappedId !== layoutId) {
            log.info(`[Sync] Wall mode: lead layout ${layoutId} → local layout ${mappedId}`);
          }
          // Follower: preload layout hidden, show comes from onLayoutShow
          log.info(`[Sync] Preparing layout ${mappedId} (waiting for show signal)`);
          await this.prepareLayout(parseInt(String(mappedId), 10));
          // Report ready to lead (use lead's layoutId so lead can track readiness)
          this.syncManager?.reportReady(layoutId);
        },
        onLayoutShow: (layoutId: string) => {
          // Map lead's layout ID to this display's layout (wall mode)
          const layoutMap = syncConfig.layoutMap || config.sync?.layoutMap;
          const mappedId = layoutMap?.[layoutId] ?? layoutId;
          const numericId = parseInt(String(mappedId), 10);

          // Compute choreography stagger delay (0 if no choreography configured)
          const choreo = syncConfig.choreography || 'simultaneous';
          const staggerMs = syncConfig.staggerMs ?? 150;

          // Build stagger options: prefer 2D topology, fall back to 1D position
          const staggerOpts: any = { choreography: choreo, staggerMs };
          if (syncConfig.topology) {
            staggerOpts.topology = syncConfig.topology;
            staggerOpts.gridCols = syncConfig.gridCols ?? 1;
            staggerOpts.gridRows = syncConfig.gridRows ?? 1;
          } else {
            staggerOpts.position = syncConfig.position ?? 0;
            staggerOpts.totalDisplays = syncConfig.totalDisplays ?? 1;
          }
          const stagger = computeStagger(staggerOpts);

          if (stagger > 0) {
            log.info(`[Sync] Show layout ${numericId} with ${stagger}ms choreography delay (${choreo})`);
            setTimeout(() => this.renderer.showLayout(numericId), stagger);
          } else {
            log.info(`[Sync] Show layout ${numericId}`);
            this.renderer.showLayout(numericId);
          }
        },
        onVideoStart: (layoutId: string, regionId: string) => {
          // Resume paused video in the specified region
          log.info(`[Sync] Video start: layout ${layoutId} region ${regionId}`);
          this.renderer.resumeRegionMedia?.(regionId);
        },
        // Lead: follower delegated stats — submit on their behalf
        onStatsReport: async (followerId: string, statsXml: string, ack: () => void) => {
          log.info(`[Sync] Submitting stats for follower ${followerId}`);
          try {
            const success = await this.xmds.submitStats(statsXml, followerId);
            if (success) ack();
          } catch (err: any) {
            log.warn(`[Sync] Stats submission failed for follower ${followerId}:`, err);
          }
        },
        // Lead: follower delegated logs — submit on their behalf
        onLogsReport: async (followerId: string, logsXml: string, ack: () => void) => {
          log.info(`[Sync] Submitting logs for follower ${followerId}`);
          try {
            const success = await this.xmds.submitLog(logsXml, followerId);
            if (success) ack();
          } catch (err: any) {
            log.warn(`[Sync] Log submission failed for follower ${followerId}:`, err);
          }
        },
        // Follower: lead confirmed our stats were submitted
        onStatsAck: async (_displayId: string) => {
          log.info('[Sync] Lead confirmed stats submission');
          if (this._pendingFollowerStats && this.statsCollector) {
            await this.statsCollector.clearSubmittedStats(this._pendingFollowerStats);
            this._pendingFollowerStats = null;
          }
        },
        // Follower: lead confirmed our logs were submitted
        onLogsAck: async (_displayId: string) => {
          log.info('[Sync] Lead confirmed logs submission');
          if (this._pendingFollowerLogs && this.logReporter) {
            await this.logReporter.clearSubmittedLogs(this._pendingFollowerLogs);
            this._pendingFollowerLogs = null;
          }
        },
        // Relay: group membership changed (auto-detect totalDisplays)
        onGroupUpdate: (totalDisplays: number, topology: Record<string, any>) => {
          log.info(`[Sync] Group update: ${totalDisplays} displays, topology: ${JSON.stringify(topology)}`);
          syncConfig.totalDisplays = totalDisplays;
        },
      });
      this.core.setSyncManager(this.syncManager);
      this.syncManager.start();
      log.info(`[Sync] SyncManager started as ${syncConfig.isLead ? 'LEAD' : 'FOLLOWER'}`);
      this.updateConfigDisplay();
    });
  }

  /**
   * Setup download and cache event handlers.
   * Handles FILES_RECEIVED, DOWNLOAD_REQUEST, PURGE_REQUEST, PURGE_ALL_REQUEST.
   */
  private setupDownloadEventHandlers() {
    this.core.on(E.FILES_RECEIVED, (files: any[]) => {
      this.updateStatus(`Downloading ${files.length} files...`);
    });

    this.core.on(E.DOWNLOAD_REQUEST, async (groupedFiles: any) => {
      // Download orchestration runs in main thread — no SW messaging
      this.downloadOverlay?.startUpdating();
      try {
        // Push current JWT token to proxy for cache-through CMS requests
        const token = this.xmds?.getToken?.() || null;
        if (token) {
          await fetch('/auth-token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });
        }
        await this.enqueueDownloads(groupedFiles);
        log.info('Download enqueue complete');
      } catch (error) {
        log.error('Download request failed:', error);
        this.updateStatus('Download failed: ' + error, 'error');
      }
    });

    this.core.on(E.PURGE_REQUEST, async (purgeFiles: any[]) => {
      try {
        const result = await store.remove(purgeFiles);
        log.info(`Purge complete: ${result.deleted}/${result.total} files deleted`);
      } catch (error) {
        log.warn('Purge failed:', error);
      }
    });

    this.core.on(E.PURGE_ALL_REQUEST, async () => {
      log.info('Purging all cached content...');
      this.updateStatus('Purging cache...');
      try {
        // Delete all files from ContentStore
        const allFiles = await store.list();
        if (allFiles.length > 0) {
          const result = await store.remove(allFiles);
          log.info(`Purged ${result.deleted} files from ContentStore`);
        }
        // Clean up any legacy Cache API caches (pre-ContentStore migration)
        const cacheNames = await caches.keys();
        if (cacheNames.length > 0) {
          await Promise.all(cacheNames.map(name => caches.delete(name)));
          log.info(`Purged ${cacheNames.length} legacy caches`);
        }
      } catch (error) {
        log.error('Cache purge failed:', error);
      }
    });
  }

  /**
   * Setup command execution event handlers.
   * Handles EXECUTE_NATIVE_COMMAND, COMMAND_RESULT, SCHEDULED_COMMAND.
   */
  private setupCommandEventHandlers() {
    // Native command execution (#202) — shell commands delegated by PlayerCore
    // Electron: use IPC (in-process, faster). Chromium/other: HTTP to proxy server.
    this.core.on(E.EXECUTE_NATIVE_COMMAND, async (data: any) => {
      let result;
      if ((window as any).electronAPI?.executeShellCommand) {
        result = await (window as any).electronAPI.executeShellCommand({
          commandString: data.commandString,
        });
      } else {
        try {
          const resp = await fetch('/shell-command', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ commandString: data.commandString }),
          });
          result = await resp.json();
        } catch (err: any) {
          result = { success: false, reason: err.message };
        }
      }
      this.core.emit(E.COMMAND_RESULT, { code: data.code, ...result });
    });

    // Command execution result
    this.core.on(E.COMMAND_RESULT, (result: any) => {
      log.info('Command result:', result);
      if (!result.success) {
        this.reportFault('COMMAND_FAILED', `Command ${result.code} failed: ${result.reason || 'unknown'}`);
      }
    });

    // Scheduled commands (#17) — execute commands whose scheduled time has arrived
    this.core.on(E.SCHEDULED_COMMAND, (command: any) => {
      log.info(`Scheduled command: ${command.code}`);
      this.core.executeCommand(command.code);
    });
  }


  /**
   * Setup Interactive Control handler (receives messages from SW for widget IC requests)
   * IC library in widget iframes makes XHR to /player/pwa/ic/*, SW forwards here.
   */
  private setupInteractiveControl() {
    this._swIcHandler = (event: any) => {
      if (event.data?.type !== 'INTERACTIVE_CONTROL') return;

      const { method, path, search, body } = event.data;
      const port = event.ports?.[0];
      if (!port) return;

      const response = this.handleInteractiveControl(method, path, search, body);
      port.postMessage(response);
    };
    navigator.serviceWorker?.addEventListener('message', this._swIcHandler);
  }

  /**
   * Notify widget iframes when DataConnector data changes.
   * XIC library listens for postMessage { ctrl: 'rtNotifyData', data: { dataKey } }
   * and calls the widget's registered notifyData callback.
   */
  private setupDataConnectorNotify() {
    const dcManager = this.core.getDataConnectorManager();
    dcManager.on('data-changed', (dataKey: string) => {
      const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe');
      const message = { ctrl: 'rtNotifyData', data: { dataKey } };
      for (const iframe of iframes) {
        try {
          iframe.contentWindow?.postMessage(message, '*');
        } catch { /* cross-origin iframe, ignore */ }
      }
    });
  }

  /**
   * Install a shared DatasourceClient and expose it to widget iframes via
   * postMessage. See packages/datasource for protocol details.
   *
   * Solves #235 gap G9:
   *   - Deduplicates fetches: N widgets on the same URL share one poller.
   *   - Caches with TTL so repeated subscriptions within the refresh window
   *     are instant.
   *   - Persists last-known-good to localStorage so cold boots show
   *     yesterday's data instead of a blank while the network warms up.
   *   - Bridges over postMessage so cross-origin widget iframes (or blob:
   *     URL iframes) can subscribe without direct window.xpDatasource
   *     access.
   *
   * The client is also exposed on window.xpDatasource for same-origin
   * widget HTML that prefers direct access.
   */
  private setupDatasourceBridge() {
    // Single shared client for the whole player session. Respects the
    // browser's network + storage; safe to reuse across layouts.
    this.datasourceClient = new DatasourceClient({
      // 30s default matches xp:refresh=30 (ADA convention)
      defaultRefreshMs: 30_000,
      // 10s fetch timeout prevents a dead tenant API from stalling the bridge
      fetchTimeoutMs: 10_000,
    });
    this._datasourceBridge = attachHostBridge(this.datasourceClient, window);

    // Same-origin escape hatch: widgets hosted on a same-origin blob: URL
    // (default path for translator output) can access the client directly
    // without the postMessage round-trip.
    try {
      (window as any).xpDatasource = this.datasourceClient;
    } catch { /* ignore — read-only window in sandboxed contexts */ }

    // Network online → force-refresh all cached URLs so stale LKG values
    // are replaced promptly when connectivity returns.
    window.addEventListener('online', () => {
      this.datasourceClient?.refresh().catch(() => {});
    });

    log.info('xp:datasource bridge installed (shared cache + postMessage)');
  }

  /**
   * Setup keyboard and presenter remote controls.
   * Handles arrow keys, page up/down, space for next/prev/pause,
   * and MediaSession API for multimedia keyboard keys.
   */
  private setupRemoteControls() {
    // Keep focus on main document so keyboard shortcuts work even with widget iframes.
    // Iframes steal focus — this pulls it back after a short delay so interactive
    // widgets still work momentarily but keyboard control returns to the player.
    window.addEventListener('blur', () => {
      // Don't steal focus when setup overlay is open (user is typing in iframe inputs)
      if (this.setupOverlay?.isVisible()) return;
      setTimeout(() => window.focus(), 200);
    });

    // Forward keyboard events from widget iframes to the main document.
    // Iframes have their own document, so keydown on the parent never fires
    // when an iframe has focus. We observe new iframes and attach forwarders.
    const attachIframeKeyForwarder = (iframe: HTMLIFrameElement) => {
      const tryAttach = () => {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!iframeDoc) return;
          if ((iframe as any).__keyForwarderAttached) return;
          (iframe as any).__keyForwarderAttached = true;
          iframeDoc.addEventListener('keydown', (e: KeyboardEvent) => {
            // Don't forward keys from setup overlay — user is typing in form inputs
            if (this.setupOverlay?.isVisible()) return;
            // Re-dispatch on the main document so our handler fires
            const clone = new KeyboardEvent('keydown', {
              key: e.key, code: e.code, keyCode: e.keyCode,
              ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, metaKey: e.metaKey,
              bubbles: true, cancelable: true,
            });
            if (document.dispatchEvent(clone)) return; // not prevented
            e.preventDefault();
          });
        } catch { /* cross-origin iframe, ignore */ }
      };
      iframe.addEventListener('load', tryAttach);
      tryAttach();
    };

    // Attach to existing and future iframes
    Array.from(document.querySelectorAll('iframe')).forEach(f => attachIframeKeyForwarder(f as HTMLIFrameElement));
    this._iframeObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node instanceof HTMLIFrameElement) attachIframeKeyForwarder(node);
          if (node instanceof HTMLElement) {
            node.querySelectorAll('iframe').forEach(f => attachIframeKeyForwarder(f as HTMLIFrameElement));
          }
        }
      }
    });
    this._iframeObserver.observe(document.body, { childList: true, subtree: true });

    // Read control toggles from config (injected by proxy into localStorage)
    const controls = this.getControls();
    const { keyboard: kb = {} } = controls;
    const debugOverlays = kb.debugOverlays === true;
    const setupKey = kb.setupKey === true;
    const playbackControl = kb.playbackControl === true;
    const videoControls = kb.videoControls === true;

    // Keyboard / presenter remote (clicker) controls
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      // Ctrl+Q — quit (Chromium kiosk: calls server /quit; Electron: handled by menu accelerator)
      if (e.key === 'q' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        log.info('[Remote] Quit requested (Ctrl+Q)');
        fetch('/quit', { method: 'POST' }).catch(() => {});
        return;
      }

      switch (e.key) {
        case 't':
        case 'T':
          if (!debugOverlays) break;
          if (!this.timelineOverlay) {
            this.timelineOverlay = new TimelineOverlay(true, (layoutId) => this.skipToLayout(layoutId));
          }
          this.timelineOverlay.toggle();
          break;
        case 'd':
        case 'D':
          if (!debugOverlays) break;
          if (!this.downloadOverlay) {
            this.downloadOverlay = new DownloadOverlay({ enabled: true, autoHide: false });
            this.downloadOverlay.setProgressCallback(() => downloadManager.getProgress());
          }
          this.downloadOverlay.toggle();
          break;
        case 'v':
        case 'V': {
          if (!videoControls) break;
          // Collect videos from parent + all same-origin iframes (widget regions)
          const allVideos: HTMLVideoElement[] = [...document.querySelectorAll<HTMLVideoElement>('video')];
          document.querySelectorAll<HTMLIFrameElement>('iframe').forEach(iframe => {
            try { allVideos.push(...iframe.contentDocument!.querySelectorAll<HTMLVideoElement>('video')); } catch {}
          });
          const show = allVideos.length > 0 && !allVideos[0].controls;
          allVideos.forEach(v => v.controls = show);
          break;
        }
        // Playback control: next/prev/pause
        case 'ArrowRight':
        case 'PageDown':
          if (!playbackControl) break;
          log.info('[Remote] Next layout (keyboard)');
          this.core.advanceToNextLayout();
          e.preventDefault();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          if (!playbackControl) break;
          log.info('[Remote] Previous layout (keyboard)');
          this.core.advanceToPreviousLayout();
          e.preventDefault();
          break;
        case ' ':
          if (!playbackControl) break;
          log.info('[Remote] Toggle pause (keyboard)');
          if (this.renderer.isPaused()) {
            this.renderer.resume();
          } else {
            this.renderer.pause();
          }
          e.preventDefault();
          break;
        case 'r':
        case 'R':
          if (!playbackControl) break;
          if (this.core.isLayoutOverridden()) {
            log.info('[Remote] Revert to schedule (keyboard)');
            this.core.revertToSchedule();
          }
          break;
        case 's':
        case 'S':
          if (!setupKey) break;
          if (!this.setupOverlay) {
            this.setupOverlay = new SetupOverlay();
          }
          this.setupOverlay.toggle();
          e.preventDefault(); // prevent 's' from being typed into the focused input
          break;
      }
    });

    // MediaSession API for multimedia keys (only fires when media is active)
    if (playbackControl && 'mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('nexttrack', () => {
        log.info('[Remote] Next layout (MediaSession)');
        this.core.advanceToNextLayout();
      });
      navigator.mediaSession.setActionHandler('previoustrack', () => {
        log.info('[Remote] Previous layout (MediaSession)');
        this.core.advanceToPreviousLayout();
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        log.info('[Remote] Pause (MediaSession)');
        this.renderer.pause();
      });
      navigator.mediaSession.setActionHandler('play', () => {
        log.info('[Remote] Resume (MediaSession)');
        this.renderer.resume();
      });
    }

    log.info('Remote controls initialized (keyboard + MediaSession)');
  }

  /** Read controls config (injected by proxy from config.json into localStorage). */
  private getControls(): Record<string, any> {
    return config.controls;
  }

  /**
   * Skip to a specific layout by ID (from timeline click or XMR command).
   * Uses changeLayout() which sets a layout override — press R to revert to schedule.
   */
  private skipToLayout(layoutId: number) {
    log.info(`Skipping to layout ${layoutId} (timeline click)`);
    this.core.changeLayout(layoutId);
  }

  private parseBody(body: string | null): any {
    try { return body ? JSON.parse(body) : {}; } catch (_) { return {}; }
  }

  /**
   * Handle an Interactive Control request from a widget
   */
  private handleInteractiveControl(method: string, path: string, search: string, body: string | null): any {
    log.debug('IC request:', method, path, search);

    switch (path) {
      case '/info':
        return {
          status: 200,
          body: JSON.stringify({
            hardwareKey: config.hardwareKey,
            displayName: config.displayName,
            playerType: 'pwa',
            currentLayoutId: this.core.getCurrentLayoutId()
          })
        };

      case '/trigger': {
        const data = this.parseBody(body);
        // Forward to renderer for layout-level actions (widget navigation)
        this.renderer.emit('interactiveTrigger', {
          targetId: data.id,
          triggerCode: data.trigger
        });
        // Forward to core for schedule-level actions (layout navigation)
        if (data.trigger) {
          this.core.handleTrigger(data.trigger);
        }
        return { status: 200, body: 'OK' };
      }

      case '/duration/expire': {
        const data = this.parseBody(body);
        log.info('IC: Widget duration expire requested for', data.id);
        this.renderer.emit('widgetExpire', { widgetId: data.id });
        return { status: 200, body: 'OK' };
      }

      case '/duration/extend': {
        const data = this.parseBody(body);
        log.info('IC: Widget duration extend by', data.duration, 'for', data.id);
        this.renderer.emit('widgetExtendDuration', {
          widgetId: data.id,
          duration: parseInt(data.duration)
        });
        return { status: 200, body: 'OK' };
      }

      case '/duration/set': {
        const data = this.parseBody(body);
        log.info('IC: Widget duration set to', data.duration, 'for', data.id);
        this.renderer.emit('widgetSetDuration', {
          widgetId: data.id,
          duration: parseInt(data.duration)
        });
        return { status: 200, body: 'OK' };
      }

      case '/fault': {
        const data = this.parseBody(body);
        this.reportFault(data.code || 'WIDGET_FAULT', data.reason || 'Widget reported fault', {
          layoutId: data.layoutId,
          regionId: data.regionId,
          widgetId: data.widgetId
        });
        return { status: 200, body: 'OK' };
      }

      case '/realtime': {
        const params = new URLSearchParams(search);
        const dataKey = params.get('dataKey');
        log.debug('IC: Realtime data request for key:', dataKey);

        if (!dataKey) {
          return { status: 400, body: JSON.stringify({ error: 'Missing dataKey parameter' }) };
        }

        const dcManager = this.core.getDataConnectorManager();
        const connectorData = dcManager.getData(dataKey);

        if (connectorData === null) {
          return { status: 404, body: JSON.stringify({ error: `No data available for key: ${dataKey}` }) };
        }

        const responseBody = typeof connectorData === 'string' ? connectorData : JSON.stringify(connectorData);
        return { status: 200, body: responseBody };
      }

      case '/criteria': {
        // Return display properties/criteria that widgets can query
        // Used by widgets to adapt content based on display characteristics
        return {
          status: 200,
          body: JSON.stringify({
            displayId: config.displayId,
            hardwareKey: config.hardwareKey,
            displayName: config.displayName,
            width: window.innerWidth,
            height: window.innerHeight,
            latitude: config.latitude || null,
            longitude: config.longitude || null,
            playerType: 'pwa'
          })
        };
      }

      default:
        return { status: 404, body: JSON.stringify({ error: 'Unknown IC route' }) };
    }
  }

  /**
   * Notify PlayerCore that a file download completed.
   * Called directly from enqueueDownloads() — no SW messaging needed.
   */
  private notifyFileCached(fileId: string, fileType: string) {
    log.debug(`Download complete: ${fileType}/${fileId}`);

    if (fileType === 'layout') {
      this.core.notifyMediaReady(parseInt(fileId), fileType);
    } else if (fileType === 'media') {
      // Pass saveAs string for media files (matches pendingLayouts entries)
      const saveAs = this._fileIdToSaveAs.get(fileId) || fileId;
      this._cachedMediaKeys.add(saveAs);
      this.core.notifyMediaReady(saveAs, fileType);
    } else {
      // Dependencies, widgets, datasets — track by storeKey
      this._cachedMediaKeys.add(fileId);
    }

    // Debounced duration probe — run after downloads settle
    if (this._probeTimer) clearTimeout(this._probeTimer);
    this._probeTimer = setTimeout(() => {
      this._probeTimer = null;
      this.probeLayoutDurations().catch(() => {});
    }, 3000);

    // Debounced media status check — update timeline missing-media annotations
    if (this._mediaStatusTimer) clearTimeout(this._mediaStatusTimer);
    this._mediaStatusTimer = setTimeout(() => {
      this._mediaStatusTimer = null;
      this.checkTimelineMediaStatus().catch(() => {});
    }, 2000);
  }

  /**
   * Enqueue files for download — runs in main thread, no SW messaging.
   * Ported from MessageHandler.handleDownloadFiles() with direct callbacks.
   */
  private async enqueueDownloads(data: any) {
    const { extractMediaIdsFromXlf } = await import('@xiboplayer/sw');
    const { layoutOrder, files, layoutDependants } = data;
    // Use DownloadManager facade methods (not direct queue access)

    /** Store key = URL path without leading / and query params */
    const storeKeyFrom = (f: any) => (f.path || '').split('?')[0].replace(/^\/+/, '') || `${f.type || 'media'}/${f.id}`;

    // Build fileId→saveAs map from CMS RequiredFiles data
    for (const f of files) {
      if (f.saveAs) {
        this._fileIdToSaveAs.set(String(f.id), f.saveAs);
      }
    }
    // Build lookup maps from flat CMS file list
    const xlfFiles = new Map();
    const resources: any[] = [];
    const mediaFiles = new Map();
    const idToKeys = new Map();
    for (const f of files) {
      if (f.type === 'layout') {
        xlfFiles.set(parseInt(f.id), f);
      } else if (f.type === 'static') {
        resources.push(f);
      } else {
        const key = `${f.type}:${f.id}`;
        mediaFiles.set(key, f);
        const bareId = String(f.id);
        if (!idToKeys.has(bareId)) idToKeys.set(bareId, []);
        idToKeys.get(bareId).push(key);
      }
    }

    log.info(`Download: ${layoutOrder.length} layouts, ${mediaFiles.size} media, ${resources.length} resources`);

    // ── Step 1: Fetch + parse all XLFs (cache-through handles store/CMS) ──
    const layoutMediaMap = new Map();
    const allXlfIds = [...layoutOrder, ...[...xlfFiles.keys()].filter((id: number) => !layoutOrder.includes(id))];
    const xlfPromises = allXlfIds.map(async (layoutId: number) => {
      const xlfFile = xlfFiles.get(layoutId);
      if (!xlfFile?.path) return;

      let xlfText: string | undefined;

      // Try store first, then cache-through fetches from CMS on miss
      try {
        const headers: Record<string, string> = {};
        if (xlfFile.cmsDownloadUrl) headers['X-Cms-Download-Url'] = xlfFile.cmsDownloadUrl;
        const resp = await fetch(xlfFile.path, Object.keys(headers).length ? { headers } : undefined);
        if (resp.ok) {
          xlfText = await resp.text();
          log.info(`Fetched XLF ${layoutId} (${xlfText.length} bytes)`);
          // Store XLF in content store so prepareLayout() can find it via store.get()
          await store.put(`${STORE_PREFIX}/layouts`, String(layoutId), new Blob([xlfText], { type: 'text/xml' }));
          this.notifyFileCached(String(layoutId), 'layout');
        }
      } catch (_) {}

      if (xlfText) {
        layoutMediaMap.set(layoutId, extractMediaIdsFromXlf(xlfText, log));
      }
    });
    await Promise.allSettled(xlfPromises);
    log.info(`Parsed ${layoutMediaMap.size} XLFs`);

    // Helper: enqueue a file, attach completion callback
    const enqueueFile = async (builder: any, file: any): Promise<boolean> => {
      if (!file.path || file.path === 'null' || file.path === 'undefined') return false;

      const storeKey = storeKeyFrom(file);

      // Check if already stored on disk (200 = cached, 204 = not in store)
      try {
        const headResp = await fetch(`/store/${storeKey}`, { method: 'HEAD' });
        if (headResp.status === 200) return false;
      } catch (_) {}

      // Check if already downloading (download manager keys are type/id, not URL paths)
      const dmKey = `${file.type}/${file.id}`;
      if (downloadManager.getTask(dmKey)) return false;

      // Check for existing chunks — skip already-downloaded ones
      try {
        const mcResp = await fetch(`/store/missing-chunks/${storeKey}`);
        if (mcResp.ok) {
          const { missing, numChunks } = await mcResp.json();
          if (numChunks > 0 && missing.length < numChunks) {
            const existing = new Set<number>();
            for (let i = 0; i < numChunks; i++) {
              if (!missing.includes(i)) existing.add(i);
            }
            file.skipChunks = existing;
            log.info(`Resuming ${storeKey}: ${existing.size}/${numChunks} chunks cached, ${missing.length} to download`);
          }
        }
      } catch (_) {}

      const fileDownload = builder.addFile(file);
      if (fileDownload.state !== 'pending') return false;

      // Direct callback — no postMessage needed
      fileDownload.wait().then((blob: any) => {
        const fileSize = parseInt(file.size) || blob.size;
        log.info('Download complete:', storeKey, `(${fileSize} bytes)`);

        // Mark chunked files as complete
        if (fileSize > this._chunkConfig.chunkSize) {
          fetch('/store/mark-complete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ storeKey }),
          }).catch((e: any) => log.warn('mark-complete failed:', storeKey, e.message));
        }

        this.notifyFileCached(String(file.id), file.type);
        downloadManager.removeCompleted(dmKey);
      }).catch((err: any) => {
        log.error('Download failed:', file.id, err);
        downloadManager.removeCompleted(dmKey);
      });
      return true;
    };

    // ── Step 2: Enqueue resources (parallel HEAD checks) ──
    const resourceBuilder = downloadManager.createTaskBuilder();
    await Promise.all(resources.map(file => enqueueFile(resourceBuilder, file)));
    const resourceTasks = await resourceBuilder.build();
    if (resourceTasks.length > 0) {
      resourceTasks.push(BARRIER);
      downloadManager.enqueueOrderedTasks(resourceTasks);
    }

    // ── Step 3: For each layout in play order, merge XLF + dependants ──
    const claimed = new Set();
    const nonScheduledIds = [...layoutMediaMap.keys()].filter((id: number) => !layoutOrder.includes(id));
    const filenameToMediaId = new Map();
    for (const [key, file] of mediaFiles) {
      if (file.saveAs) filenameToMediaId.set(file.saveAs, key);
    }

    const depMap = new Map();
    if (layoutDependants) {
      for (const [id, filenames] of Object.entries(layoutDependants)) {
        depMap.set(parseInt(id, 10), filenames);
      }
    }

    for (const layoutId of layoutOrder) {
      const xlfMediaIds = layoutMediaMap.get(layoutId);
      if (!xlfMediaIds) continue;

      const bareIds = new Set(xlfMediaIds);
      for (const nsId of nonScheduledIds) {
        const nsMediaIds = layoutMediaMap.get(nsId);
        if (nsMediaIds) {
          for (const id of nsMediaIds) bareIds.add(id);
        }
      }
      const deps = depMap.get(layoutId) || [];
      for (const filename of deps) {
        const key = filenameToMediaId.get(filename);
        if (key) bareIds.add(key);
      }

      const matched: any[] = [];
      for (const bareId of bareIds) {
        if (mediaFiles.has(bareId) && !claimed.has(bareId)) {
          matched.push(mediaFiles.get(bareId));
          claimed.add(bareId);
          continue;
        }
        const keys = idToKeys.get(String(bareId)) || [];
        for (const key of keys) {
          if (claimed.has(key)) continue;
          matched.push(mediaFiles.get(key));
          claimed.add(key);
        }
      }
      if (matched.length === 0) continue;

      log.info(`Layout ${layoutId}: ${matched.length} media`);
      matched.sort((a: any, b: any) => (a.size || 0) - (b.size || 0));
      const builder = downloadManager.createTaskBuilder();
      await Promise.all(matched.map(file => enqueueFile(builder, file)));
      const orderedTasks = await builder.build();
      if (orderedTasks.length > 0) {
        orderedTasks.push(BARRIER);
        downloadManager.enqueueOrderedTasks(orderedTasks);
      }
    }

    // Enqueue unclaimed media
    const unclaimed = [...mediaFiles.keys()].filter((id: string) => !claimed.has(id));
    if (unclaimed.length > 0) {
      log.info(`${unclaimed.length} media not in any XLF`);
      const builder = downloadManager.createTaskBuilder();
      await Promise.all(unclaimed.map(id => {
        const file = mediaFiles.get(id);
        return file ? enqueueFile(builder, file) : Promise.resolve(false);
      }));
      const orderedTasks = await builder.build();
      if (orderedTasks.length > 0) {
        downloadManager.enqueueOrderedTasks(orderedTasks);
      }
    }

    log.info('Downloads active:', downloadManager.running, ', queued:', downloadManager.queued);
  }

  /**
   * Setup renderer event handlers
   */
  private setupRendererEventHandlers() {
    this.renderer.on('layoutStart', (layoutId: number, _layout: any) => {
      log.info('Layout started:', layoutId);
      this.updateStatus(`Playing layout ${layoutId}`);

      this.core.setCurrentLayout(layoutId);

      // #236: layout-tag → sync-group bridge. Inspect the incoming
      // layout's <tags> for xp-sync-group:NAME and debounce a
      // setSyncGroup() call. Falls back to renderer.getCurrentLayoutTags()
      // if the event payload didn't carry tags directly.
      const tags: string[] = Array.isArray(_layout?.tags)
        ? _layout.tags
        : (this.renderer.getCurrentLayoutTags?.() ?? []);
      this.handleLayoutSyncGroupTag(tags);

      // Store layout-level enableStat for use in layoutEnd
      this._currentLayoutEnableStat = _layout?.enableStat !== false;

      // Update timeline overlay with current layout's known duration
      const layoutDur = this.core.getLayoutDuration(layoutId) || _layout?.duration;
      this.timelineOverlay?.update(null, layoutId, layoutDur);

      // Track stats: start layout (only if enableStat is not disabled)
      if (this.statsCollector && this._currentLayoutEnableStat) {
        this.statsCollector.startLayout(layoutId, this.currentScheduleId).catch((err: any) => {
          log.error('Failed to start layout stat:', err);
        });
      }
    });

    this.renderer.on('layoutEnd', (layoutId: number) => {
      log.info('Layout ended:', layoutId);

      // Record play at END so maxPlaysPerHour doesn't interrupt the current play.
      // Previously recorded at layoutStart, which caused periodic collections to
      // filter the layout mid-playback (e.g., 200s video cut at 168s).
      scheduleManager?.recordPlay(layoutId.toString());

      // Track stats: end layout (only if enableStat was not disabled)
      if (this.statsCollector && this._currentLayoutEnableStat) {
        this.statsCollector.endLayout(layoutId, this.currentScheduleId).catch((err: any) => {
          log.error('Failed to end layout stat:', err);
        });
      }

      // If a new layout is already rendering or being prepared (async fetch),
      // skip advance — the transition was already handled by the caller.
      // Stats/play recording above still run for proper tracking.
      if (this.renderer.getCurrentLayoutId() && this.renderer.getCurrentLayoutId() !== layoutId) {
        log.debug(`Layout ${layoutId} ended but ${this.renderer.getCurrentLayoutId()} already playing, skipping advance`);
        return;
      }
      if (this.preparingLayoutId && this.preparingLayoutId !== layoutId) {
        log.debug(`Layout ${layoutId} ended but ${this.preparingLayoutId} being prepared, skipping advance`);
        return;
      }

      // Report to CMS
      this.core.notifyLayoutStatus(layoutId);

      // Clear current layout to allow replay/advance
      this.core.clearCurrentLayout();

      // If a new layout is already pending download, don't advance
      // (avoids redundant XMDS calls and duplicate download requests)
      const pending = this.core.getPendingLayouts();
      if (pending.length > 0) {
        log.info(`Layout ${pending[0]} pending download, skipping advance`);
        return;
      }

      // Advance to the next layout in the schedule (round-robin cycling)
      // This avoids a full collect() cycle — just picks the next layout and renders it.
      // Periodic collect() cycles still run on the collection interval to sync with CMS.
      log.info('Layout cycle completed, advancing to next layout...');
      this.core.advanceToNextLayout();
    });

    this.renderer.on('widgetStart', (data: any) => {
      const { widgetId, layoutId, mediaId } = data;
      log.debug('Widget started:', data.type, widgetId, 'media:', mediaId);

      // Track stats: start widget/media (only if enableStat is not disabled)
      if (this.statsCollector && mediaId && data.enableStat !== false) {
        this.statsCollector.startWidget(mediaId, layoutId, this.currentScheduleId).catch((err: any) => {
          log.error('Failed to start widget stat:', err);
        });
      }
    });

    this.renderer.on('widgetEnd', (data: any) => {
      const { widgetId, layoutId, mediaId } = data;
      log.debug('Widget ended:', data.type, widgetId, 'media:', mediaId);

      // Track stats: end widget/media (only if enableStat is not disabled)
      if (this.statsCollector && mediaId && data.enableStat !== false) {
        this.statsCollector.endWidget(mediaId, layoutId, this.currentScheduleId).catch((err: any) => {
          log.error('Failed to end widget stat:', err);
        });
      }
    });

    // Widget commands (#202) — execute commands embedded in layout widgets
    this.renderer.on('widgetCommand', (data: any) => {
      log.info('Widget command:', data.commandCode);
      const commands = { [data.commandCode]: { commandString: data.commandString } };
      this.core.executeCommand(data.commandCode, commands);
    });

    this.renderer.on('error', (error: any) => {
      log.error('Renderer error:', error);
      this.updateStatus(`Error: ${error.type}`, 'error');

      // Report fault to CMS (triggers dashboard alert)
      this.reportFault(error.type || 'RENDERER_ERROR', `Renderer error: ${error.message || error.type}`, {
        layoutId: error.layoutId,
        regionId: error.regionId,
        widgetId: error.widgetId
      });
    });

    // Handle interactive actions from touch/click and keyboard triggers
    this.renderer.on('action-trigger', (data: any) => {
      const { actionType, triggerCode, layoutCode, targetId, commandCode } = data;
      log.info('Action trigger:', actionType, data);

      switch (actionType) {
        case 'navLayout':
        case 'navigateToLayout':
          if (triggerCode) {
            this.core.handleTrigger(triggerCode);
          } else if (layoutCode) {
            this.core.changeLayout(layoutCode);
          }
          break;

        case 'navWidget':
        case 'navigateToWidget':
          if (triggerCode) {
            this.core.handleTrigger(triggerCode);
          } else if (targetId) {
            this.renderer.navigateToWidget(targetId);
          }
          break;

        case 'previousWidget':
          this.renderer.previousWidget(data.source?.regionId);
          break;

        case 'nextWidget':
          this.renderer.nextWidget(data.source?.regionId);
          break;

        case 'command':
          if (commandCode) {
            this.core.executeCommand(commandCode);
          }
          break;

        default:
          log.warn('Unknown action type:', actionType);
      }

      // Record interaction event for proof of play (#19)
      if (this.statsCollector) {
        this.statsCollector.recordEvent('touch', this.core.getCurrentLayoutId(), data.targetId || null, this.currentScheduleId);
      }
    });

    // Widget duration webhooks (#16) — fire HTTP POST when widget duration expires
    this.renderer.on('widgetAction', (data: any) => {
      if (data.type === 'durationEnd' && data.url) {
        log.info(`Widget ${data.widgetId} duration ended, calling webhook: ${data.url}`);

        // Record webhook event for proof of play (#19)
        if (this.statsCollector) {
          this.statsCollector.recordEvent('webhook', data.layoutId, data.widgetId, this.currentScheduleId);
        }

        fetch(data.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            widgetId: data.widgetId,
            layoutId: data.layoutId,
            regionId: data.regionId,
            event: 'durationEnd',
            timestamp: new Date().toISOString()
          })
        }).catch(err => log.warn('Webhook failed (non-critical):', err));
      }
    });

    // Correct timeline duration when video metadata reveals actual duration
    this.renderer.on('layoutDurationUpdated', (layoutId: number, duration: number, final: boolean) => {
      this.core.recordLayoutDuration(String(layoutId), duration, final);
    });

    // Handle next layout preload request from renderer
    // Fired at 75% of current layout duration to pre-build the next layout's DOM
    this.renderer.on('request-next-layout-preload', async () => {
      try {
        // Peek at the next layout without advancing the schedule index
        const next = this.core.peekNextLayout();
        if (!next) {
          log.debug('No next layout to preload (single layout schedule or same layout)');
          return;
        }

        const nextLayoutId = next.layoutId;

        // Skip if already preloaded or preload in-flight
        if (this.renderer.layoutPool.has(nextLayoutId)) {
          log.debug(`Layout ${nextLayoutId} already in preload pool`);
          return;
        }
        if ((this.renderer as any)._preloadingLayoutId === nextLayoutId) {
          log.debug(`Layout ${nextLayoutId} preload already in-flight`);
          return;
        }

        log.info(`Preloading next layout ${nextLayoutId}...`);

        // Get XLF from cache
        const xlfBlob = await store.get(`${STORE_PREFIX}/layouts`, nextLayoutId);
        if (!xlfBlob) {
          log.debug(`Layout ${nextLayoutId} XLF not cached, skipping preload`);
          return;
        }

        const xlfXml = await xlfBlob.text();
        const doc = new DOMParser().parseFromString(xlfXml, 'text/xml');

        // Check if all required media is cached
        const { allMedia: requiredMedia } = this.getMediaIds(doc);
        const allMediaCached = await this.checkAllMediaCached(requiredMedia);

        if (!allMediaCached) {
          log.debug(`Media not fully cached for layout ${nextLayoutId}, skipping preload`);
          return;
        }

        // Fetch widget HTML before preloading (same as prepareLayout)
        await this.fetchWidgetHtml(doc, nextLayoutId);

        // Preload the layout into the renderer's pool
        const success = await this.renderer.preloadLayout(xlfXml, nextLayoutId);
        if (success) {
          log.info(`Layout ${nextLayoutId} preloaded successfully`);
        } else {
          log.warn(`Layout ${nextLayoutId} preload failed (will fall back to normal render)`);
        }
      } catch (error) {
        log.warn('Layout preload failed (non-blocking):', error);
        // Non-blocking: preload failure is graceful, normal render path will be used
      }
    });

    // Handle video playback errors — re-download only missing chunks
    this.renderer.on('videoError', async ({ storedAs }: any) => {
      if (!storedAs) return;
      const storeKey = `${PLAYER_API.slice(1)}/media/file/${storedAs}`;
      try {
        const resp = await fetch(`/store/missing-chunks/${storeKey}`);
        const { missing } = await resp.json();
        if (missing.length === 0) {
          log.warn(`Video ${storedAs}: corrupt file (all chunks present), deleting for re-download`);
          await fetch('/store/delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ files: [{ key: storeKey }] }),
          });
          const layoutId = this.core.getCurrentLayoutId();
          if (layoutId) {
            this.core.setPendingLayout(layoutId, [storedAs]);
          }
          this.core.collectNow().catch((err: any) => {
            log.error(`Failed to trigger re-download for ${storedAs}:`, err.message);
          });
          return;
        }
        log.warn(`Video ${storedAs}: ${missing.length} missing chunks (${missing.join(', ')}), re-downloading`);

        // Unmark completion (keeps existing chunks on disk) so HEAD returns 404
        await fetch('/store/unmark-complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ storeKey }),
        });

        // Trigger collection — enqueueFile will populate skipChunks for existing chunks
        this.core.collectNow().catch((err: any) => {
          log.error(`Failed to trigger re-download for ${storedAs}:`, err.message);
        });
      } catch (err: any) {
        log.error(`Failed to check/re-download ${storedAs}:`, err.message);
      }
    });
  }

  /**
   * Prepare and render layout (Platform-specific logic)
   */
  private async prepareLayout(layoutId: number) {
    // Same layout replay — use renderer's built-in replay path which
    // re-emits layoutStart, restarts timer and widget cycling.
    if (this.renderer.getCurrentLayoutId() === layoutId) {
      log.debug(`Layout ${layoutId} replay`);
      this.core.clearPreparingLayout();
      // Renderer's same-layout replay path reuses existing DOM — XLF not re-parsed
      await this.renderer.renderLayout('', layoutId);
      return;
    }

    // Guard: prevent concurrent preparations of the same layout.
    // Instead of dropping the event (which caused permanent stalls when the
    // first attempt failed due to a store race), schedule a retry after
    // the current preparation finishes.
    if (this.preparingLayoutId === layoutId) {
      log.debug(`Layout ${layoutId} preparation in progress, will retry after it completes`);
      this._pendingRetryLayoutId = layoutId;
      return;
    }

    this.preparingLayoutId = layoutId;
    try {
      // Get XLF from cache
      const xlfBlob = await store.get(`${STORE_PREFIX}/layouts`, layoutId);
      if (!xlfBlob) {
        log.info('Layout not in cache yet, marking as pending:', layoutId);
        // Mark layout as pending so when it downloads, we'll retry
        // Use layoutId as required file (will trigger on layout file cached)
        this.core.setPendingLayout(layoutId, [String(layoutId)]);
        this.updateStatus(`Downloading layout ${layoutId}...`);
        return;
      }

      const xlfXml = await xlfBlob.text();

      // Parse XLF once — reuse Document for media check and widget HTML fetch
      const xlfDoc = new DOMParser().parseFromString(xlfXml, 'text/xml');

      // Check if all required media is cached
      const { allMedia: requiredMedia } = this.getMediaIds(xlfDoc);
      const allMediaCached = await this.checkAllMediaCached(requiredMedia);

      if (!allMediaCached) {
        // Reorder download queue: current layout's media first, hold others.
        // All files (including all chunks) must complete before other layouts start.
        downloadManager.prioritizeLayoutFiles(requiredMedia.map(String));

        log.info(`Waiting for media to finish downloading for layout ${layoutId}`);
        this.updateStatus(`Preparing layout ${layoutId}...`);
        this.core.setPendingLayout(layoutId, requiredMedia);
        return; // Keep playing current layout until media is ready
      }

      // Fetch widget HTML (skip if already preloaded — was fetched during preload)
      if (!this.renderer.hasPreloadedLayout(layoutId)) {
        await this.fetchWidgetHtml(xlfDoc, layoutId);
      }

      // Preload layout into pool (hidden). Caller decides when to show.
      await this.renderer.preloadLayout(xlfXml, layoutId);

      // Clear pending status — layout prepared successfully, all media cached.
      // Without this, pendingLayouts keeps the layout marked as ⚠ missing
      // until it actually plays (which may be minutes later in the rotation).
      this.core.pendingLayouts.delete(layoutId);

      log.info(`Layout ${layoutId} ready`);

    } catch (error: any) {
      log.error('Failed to prepare layout:', layoutId, error);
      this.updateStatus(`Failed to load layout ${layoutId}`, 'error');

      // Report fault to CMS (triggers dashboard alert)
      this.reportFault('LAYOUT_LOAD_FAILED', `Failed to prepare layout ${layoutId}: ${error?.message || error}`, {
        layoutId
      });
    } finally {
      this.preparingLayoutId = null;
      this.core.clearPreparingLayout();

      // If another check-pending-layout arrived while we were preparing,
      // retry after a short delay to let the ContentStore settle.
      // This fixes the race where FILE_CACHED notification arrives before
      // the PUT to ContentStore is visible to HEAD requests.
      const retryId = this._pendingRetryLayoutId;
      this._pendingRetryLayoutId = null;
      if (retryId !== null && retryId !== undefined && this.core.getCurrentLayoutId() !== retryId) {
        log.debug(`Retrying preparation for layout ${retryId} after 500ms`);
        setTimeout(() => this.prepareLayout(retryId), 500);
      }
    }
  }

  /**
   * Get all required media saveAs filenames and video-specific ones from layout XLF.
   * Returns saveAs strings (via _fileIdToSaveAs map) for store key matching.
   */
  private getMediaIds(xlfXmlOrDoc: string | Document): { allMedia: string[]; videoMedia: string[] } {
    const doc = typeof xlfXmlOrDoc === 'string'
      ? new DOMParser().parseFromString(xlfXmlOrDoc, 'text/xml')
      : xlfXmlOrDoc;
    const allMedia: string[] = [];
    const videoMedia: string[] = [];

    doc.querySelectorAll('media[fileId]').forEach(el => {
      const fileId = el.getAttribute('fileId');
      if (fileId) {
        const saveAs = this._fileIdToSaveAs.get(fileId) || fileId;
        // Skip layout XLF references — stored in layouts/ store, not media/file/
        if (saveAs.endsWith('.xlf')) return;
        allMedia.push(saveAs);
        if (el.getAttribute('type') === 'video') {
          videoMedia.push(saveAs);
        }
      }
    });

    // Include background image file ID from layout element
    const bgFileId = doc.querySelector('layout')?.getAttribute('background');
    if (bgFileId) {
      const saveAs = this._fileIdToSaveAs.get(bgFileId) || bgFileId;
      if (!allMedia.includes(saveAs)) {
        allMedia.push(saveAs);
      }
    }

    return { allMedia, videoMedia };
  }

  /**
   * Check if all required media files are cached and ready.
   * Uses StoreClient.has() → HEAD /store${PLAYER_API}/media/:id to check ContentStore.
   */
  /**
   * Check if all required media files are cached and ready.
   * Uses storedAs filenames for store key matching: /media/file/{saveAs}
   */
  private async checkAllMediaCached(mediaSaveAs: string[]): Promise<boolean> {
    // Check in-memory set first — avoids all HEAD requests for known-cached files
    const unknown = mediaSaveAs.filter(s => !this._cachedMediaKeys.has(s));
    if (unknown.length === 0) return true;

    // HEAD-check all unknown files against the content store.
    // Always check the store directly — the download queue may have stale tasks
    // for files that are already cached (race between download completion and
    // task cleanup). The HEAD check is fast (<1ms for local store) and authoritative.
    const toCheck = unknown;

    const results = await Promise.all(
      toCheck.map(async (saveAs) => {
        try {
          const cached = await store.has(STORE_PREFIX, `media/file/${saveAs}`);
          if (cached) this._cachedMediaKeys.add(saveAs);
          return cached;
        } catch {
          log.warn(`Unable to verify media ${saveAs}, assuming cached (offline mode)`);
          return true;
        }
      })
    );
    const missing = toCheck.filter((_, i) => !results[i]);
    if (missing.length > 0) {
      log.debug(`Media not yet cached: ${missing.join(', ')}`);
      return false;
    }
    return true;
  }

  /**
   * Fetch widget HTML for all widgets in layout (parallel)
   */
  private async fetchWidgetHtml(xlfXmlOrDoc: string | Document, layoutId: number) {
    const doc = typeof xlfXmlOrDoc === 'string'
      ? new DOMParser().parseFromString(xlfXmlOrDoc, 'text/xml')
      : xlfXmlOrDoc;

    const fetchPromises: Promise<void>[] = [];

    for (const regionEl of doc.querySelectorAll('region')) {
      const regionId = regionEl.getAttribute('id');

      for (const mediaEl of regionEl.querySelectorAll('media')) {
        const type = mediaEl.getAttribute('type');
        const widgetId = mediaEl.getAttribute('id');
        const render = mediaEl.getAttribute('render');

        // XLF render="html" means CMS provides pre-rendered HTML via getResource.
        // render="native" means player handles the media directly (video, image, audio).
        if (render === 'html') {
          fetchPromises.push(
            (async () => {
              try {
                // Check ContentStore for existing widget HTML
                const storeId = `${layoutId}/${regionId}/${widgetId}`;
                let html: string | null = null;

                const existing = await store.get(`${STORE_PREFIX}/widgets`, storeId);
                if (existing) {
                  html = await existing.text();
                  log.debug(`Found cached widget HTML for ${type} ${widgetId}`);
                }

                if (!html) {
                  html = await this.xmds.getResource(layoutId, regionId, widgetId);
                  log.debug(`Retrieved widget HTML for ${type} ${widgetId} from CMS`);
                }
                // Always process: injects <base> tag, rewrites IC hostAddress.
                // cacheWidgetHtml is idempotent — already-rewritten URLs won't re-match.
                const result = await cacheWidgetHtml(layoutId, regionId, widgetId, html);
                html = result.html;

                // Update raw content in XLF
                const rawEl = mediaEl.querySelector('raw');
                if (rawEl) {
                  rawEl.textContent = html;
                } else {
                  const newRaw = doc.createElement('raw');
                  newRaw.textContent = html;
                  mediaEl.appendChild(newRaw);
                }
              } catch (error) {
                log.warn(`Failed to get widget HTML for ${type} ${widgetId}:`, error);
              }
            })()
          );
        }
      }
    }

    if (fetchPromises.length > 0) {
      log.info(`Fetching ${fetchPromises.length} widget HTML resources in parallel...`);
      await Promise.all(fetchPromises);
      log.debug('All widget HTML fetched');
    }
  }

  /**
   * Check media cache status for all scheduled layouts.
   * For each layout: load XLF from cache, extract media IDs, check each with store.has().
   * Feeds results into PlayerCore.setLayoutMediaStatus() for timeline annotation.
   */
  private async checkTimelineMediaStatus() {
    if (this.scheduledLayoutIds.size === 0) return;

    for (const layoutId of this.scheduledLayoutIds) {
      const layoutFile = `${layoutId}.xlf`;
      try {
        const xlfBlob = await store.get(`${STORE_PREFIX}/layouts`, layoutId);
        if (!xlfBlob) continue;

        const xlfXml = await xlfBlob.text();
        const { allMedia } = this.getMediaIds(xlfXml);

        if (allMedia.length === 0) {
          this.core.setLayoutMediaStatus(layoutFile, true);
          continue;
        }

        const missing: string[] = [];
        for (const saveAs of allMedia) {
          if (this._cachedMediaKeys.has(saveAs)) continue;
          // If in download queue, it's not cached — skip HEAD
          const storeKey = `${STORE_PREFIX}/media/file/${saveAs}`;
          if (downloadManager.getTask(storeKey)) { missing.push(saveAs); continue; }
          try {
            const cached = await store.has(STORE_PREFIX, `media/file/${saveAs}`);
            if (cached) this._cachedMediaKeys.add(saveAs);
            else missing.push(saveAs);
          } catch {
            // Assume cached on error (offline mode)
          }
        }

        this.core.setLayoutMediaStatus(layoutFile, missing.length === 0, missing);
      } catch {
        // Skip layouts we can't load
      }
    }

    // Re-emit annotated timeline
    this.core.logUpcomingTimeline();
  }

  /**
   * Probe video durations for all scheduled layouts.
   * Uses preload="metadata" — only fetches headers (~50KB), not the full video.
   * Feeds discovered durations into PlayerCore for accurate timeline calculation.
   */
  private async probeLayoutDurations() {
    if (this.scheduledLayoutIds.size === 0) return;

    for (const layoutId of this.scheduledLayoutIds) {

      try {
        const xlfBlob = await store.get(`${STORE_PREFIX}/layouts`, layoutId);
        if (!xlfBlob) continue;

        const xlfXml = await xlfBlob.text();
        const { videoMedia } = this.getMediaIds(xlfXml);
        if (videoMedia.length === 0) continue;

        // Parse XLF to find video widgets with duration=0 (use media length)
        const parser = new DOMParser();
        const doc = parser.parseFromString(xlfXml, 'text/xml');

        // Probe actual video durations, keyed by fileId
        const videoDurations = new Map<string, number>();
        let dynamicVideoCount = 0;
        for (const mediaEl of doc.querySelectorAll('media[type="video"]')) {
          const useDuration = mediaEl.getAttribute('useDuration');
          if (useDuration === '1') continue; // Has explicit CMS duration, skip

          const fileId = mediaEl.getAttribute('fileId');
          if (!fileId) continue;
          dynamicVideoCount++;

          const saveAs = this._fileIdToSaveAs.get(fileId) || fileId;
          const exists = await store.has(STORE_PREFIX, `media/file/${saveAs}`);
          if (!exists) continue;

          // Probe metadata only — does NOT download the full video
          const duration = await this.probeVideoDuration(`${window.location.origin}${PLAYER_API}/media/file/${saveAs}`);
          if (duration > 0) {
            videoDurations.set(fileId, duration);
          }
        }

        if (videoDurations.size === 0) continue;

        // Only mark final if ALL dynamic videos were successfully probed
        const allProbed = videoDurations.size >= dynamicVideoCount;

        // Phase 2: refine layout duration with probed video lengths
        const { duration: probedDuration } = parseLayoutDuration(xlfXml, videoDurations);
        if (probedDuration > 0) {
          this.core.recordLayoutDuration(String(layoutId), probedDuration, allProbed);
        }
      } catch (err) {
        log.debug(`Duration probe failed for layout ${layoutId}:`, err);
      }
    }
  }

  /**
   * Probe a single video's duration using metadata only.
   * Creates a temporary <video preload="metadata"> element, reads duration, destroys it.
   */
  private probeVideoDuration(url: string): Promise<number> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;

      const cleanup = () => {
        video.removeAttribute('src');
        video.load(); // Release resources
      };

      video.addEventListener('loadedmetadata', () => {
        const dur = video.duration;
        cleanup();
        resolve(dur);
      }, { once: true });

      video.addEventListener('error', () => {
        cleanup();
        resolve(0);
      }, { once: true });

      // Safety timeout — don't block forever
      setTimeout(() => {
        cleanup();
        resolve(0);
      }, 5000);

      video.src = url;
    });
  }

  /**
   * Update config display
   */
  private updateConfigDisplay() {
    const configEl = document.getElementById('config-info');
    if (configEl) {
      const version = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '?';
      const buildDate = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__.replace('T', ' ').replace(/\.\d+Z$/, '') : '';
      const versionStr = buildDate ? `v${version} (${buildDate})` : `v${version}`;
      let text = `${versionStr} | CMS: ${config.cmsUrl} | Display: ${config.displayName || 'Unknown'} | HW: ${config.hardwareKey}`;
      const sc = this.core?.getSyncConfig?.();
      if (sc) {
        const relay = sc.relayUrl ? new URL(sc.relayUrl).host : '';
        text += ` | Sync: ${sc.isLead ? 'LEAD' : `FOLLOWER → ${relay}`} (group ${sc.syncGroupId || sc.syncGroup})`;
      }
      configEl.textContent = text;
    }
  }

  /**
   * Generic submission pipeline for stats and logs.
   * Handles in-flight guard, sync delegation, CMS submission, and cleanup.
   */
  private async submitCollectedData(options: {
    name: string;
    pendingFlag: '_pendingFollowerStats' | '_pendingFollowerLogs';
    getItems: () => Promise<any[]>;
    formatFn: (items: any[]) => string;
    delegateFn: (xml: string) => void;
    submitFn: (xml: string) => Promise<any>;
    clearFn: (items: any[]) => Promise<void>;
  }): Promise<void> {
    const { name, pendingFlag, getItems, formatFn, delegateFn, submitFn, clearFn } = options;

    // Guard: don't start a new delegation while one is in-flight
    if (this[pendingFlag] !== null) {
      log.debug(`${name} delegation in-flight, skipping`);
      return;
    }

    try {
      const items = await getItems();

      if (items.length === 0) {
        log.debug(`No ${name} to submit`);
        return;
      }

      const xml = formatFn(items);

      // Follower with live lead: delegate via BroadcastChannel
      if (this.syncManager && !this.syncManager.isLead && this._syncLeadAlive()) {
        log.info(`[Sync] Delegating ${items.length} ${name} to lead`);
        this[pendingFlag] = items;
        delegateFn(xml);
        return;
      }

      // Lead, standalone, or lead-dead follower: submit directly
      if (this.syncManager && !this.syncManager.isLead) {
        log.warn(`[Sync] Lead not alive, submitting ${name} directly`);
      }

      log.info(`Submitting ${items.length} ${name} to CMS...`);

      const success = await submitFn(xml);

      if (success) {
        log.info(`${name} submitted successfully`);
        await clearFn(items);
      } else {
        log.warn(`${name} submission failed (CMS returned false)`);
      }
    } catch (error) {
      log.error(`Failed to submit ${name}:`, error);
    }
  }

  /**
   * Submit proof of play stats to CMS
   */
  private async submitStats(): Promise<void> {
    if (!this.statsCollector) {
      log.warn('Stats collector not initialized');
      return;
    }

    const aggregationLevel = this.displaySettings?.getSetting('aggregationLevel') || 'Individual';

    await this.submitCollectedData({
      name: 'stats',
      pendingFlag: '_pendingFollowerStats',
      getItems: async () => aggregationLevel === 'Aggregate'
        ? this.statsCollector.getAggregatedStatsForSubmission(50)
        : this.statsCollector.getStatsForSubmission(50),
      formatFn: formatStats,
      delegateFn: (xml) => this.syncManager.reportStats(xml),
      submitFn: (xml) => this.xmds.submitStats(xml),
      clearFn: (items) => this.statsCollector.clearSubmittedStats(items),
    });
  }

  /**
   * Submit player logs to CMS for remote debugging
   */
  private async submitLogs(): Promise<void> {
    if (!this.logReporter) return;

    await this.submitCollectedData({
      name: 'logs',
      pendingFlag: '_pendingFollowerLogs',
      getItems: () => this.logReporter.getLogsForSubmission(),
      formatFn: formatLogs,
      delegateFn: (xml) => this.syncManager.reportLogs(xml),
      submitFn: (xml) => this.xmds.submitLog(xml),
      clearFn: (items) => this.logReporter.clearSubmittedLogs(items),
    });
  }

  /**
   * Report a fault to both the log dashboard and the player_faults dashboard.
   * Combines logReporter.reportFault() (log dashboard) with submitFault() (faults dashboard).
   */
  private reportFault(code: string, reason: string, details?: { layoutId?: number; regionId?: string; widgetId?: string }): void {
    this.logReporter?.reportFault(code, reason);
    this.submitFault(code, reason, details);
  }

  /**
   * Submit a fault report to CMS for the player_faults dashboard.
   */
  private submitFault(code: string, reason: string, details?: { layoutId?: number; regionId?: string; widgetId?: string }) {
    if (!this.xmds) return;

    const fault = JSON.stringify([{
      code,
      reason,
      date: new Date().toISOString().replace('T', ' ').substring(0, 19),
      ...details
    }]);

    this.xmds.reportFaults(fault).catch((err: any) => {
      log.debug('reportFaults failed (non-critical):', err);
    });
  }

  /**
   * Capture screenshot and submit to CMS.
   *
   * Strategy (best available, tried in order):
   *  0. Electron IPC — webContents.capturePage() via preload bridge.
   *     Pixel-perfect, captures video/WebGL/composited layers, zero DOM cost.
   *     Only available when running inside the Electron shell.
   *  1. getDisplayMedia() — native pixel capture via screen sharing API.
   *     Pixel-perfect, zero DOM manipulation. Chromium kiosk auto-approves
   *     via --auto-select-desktop-capture-source flag.
   *  2. Direct canvas drawing — fallback that draws img/video/canvas elements
   *     directly. Text-only widgets (clocks, tickers) won't appear.
   *
   * The first successful method is cached for subsequent calls.
   */
  private async captureAndSubmitScreenshot() {
    // Concurrency guard — skip if a capture is already in flight
    if (this._screenshotInFlight) {
      log.debug('Screenshot capture already in progress, skipping');
      return;
    }
    this._screenshotInFlight = true;

    try {
      let base64: string;

      // Electron path: use native webContents.capturePage() via IPC
      if (this._screenshotMethod === 'electron' ||
          (this._screenshotMethod === null && (window as any).electronAPI?.captureScreenshot)) {
        const electronResult = await (window as any).electronAPI.captureScreenshot();
        if (electronResult) {
          this._screenshotMethod = 'electron';
          base64 = electronResult;
        } else {
          // Electron capture returned null (window not yet painted).
          // Do NOT fall through to getDisplayMedia — it triggers a
          // permission dialog that blocks the whole UI.  Skip this
          // cycle; capturePage() will succeed on the next interval.
          log.debug('Electron screenshot not ready yet, will retry next interval');
          return;
        }
      } else if (this._screenshotMethod === 'displayMedia' ||
                 (this._screenshotMethod === null && typeof navigator.mediaDevices?.getDisplayMedia === 'function')) {
        // Try getDisplayMedia — pixel-perfect screen capture, zero DOM cost.
        // Chromium kiosk auto-approves via --auto-accept-this-tab-capture.
        try {
          base64 = await this.captureDisplayMedia();
          this._screenshotMethod = 'displayMedia';
        } catch (e: any) {
          log.warn('getDisplayMedia failed, falling back to html2canvas:', e.message || e);
          this._screenshotMethod = null;
          base64 = await this.captureHtml2CanvasIsolated();
          this._screenshotMethod = 'html2canvas';
        }
      } else {
        // Tier 3: html2canvas hybrid (Firefox, other browsers)
        // Direct draw for img/video/canvas + per-iframe html2canvas for HTML widgets
        this._screenshotMethod = 'html2canvas';
        base64 = await this.captureHtml2CanvasIsolated();
      }

      const success = await this.xmds.submitScreenShot(base64);
      if (success) {
        log.info(`Screenshot submitted (${this._screenshotMethod})`);
      } else {
        log.warn('Screenshot submission failed');
      }
    } catch (error) {
      log.error('Failed to capture screenshot:', error);
    } finally {
      this._screenshotInFlight = false;
    }
  }

  /**
   * Capture screenshot via getDisplayMedia (screen sharing API).
   * Pixel-perfect, captures everything the GPU renders including video,
   * WebGL, composited layers, and all widget content.
   * Chromium kiosk auto-approves via --auto-select-desktop-capture-source.
   */
  private async captureDisplayMedia(): Promise<string> {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: false,
      preferCurrentTab: true,
    } as any);

    try {
      const track = stream.getVideoTracks()[0];
      const imageCapture = new (window as any).ImageCapture(track);
      const bitmap = await imageCapture.grabFrame();

      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(bitmap, 0, 0);
      bitmap.close();

      return canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
    } finally {
      stream.getTracks().forEach(t => t.stop());
    }
  }

  /**
   * Capture screenshot via html2canvas hybrid approach.
   * Draws images/video/canvas directly, uses per-iframe html2canvas for
   * HTML widgets (clocks, tickers). CSS contain: strict on capture divs
   * prevents ResizeObserver glitches.
   */
  private async captureHtml2CanvasIsolated(): Promise<string> {
    const canvas = document.createElement('canvas');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const container = document.getElementById('player-container');
    if (!container) {
      return canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
    }

    // Lazy-load html2canvas
    if (!this._html2canvasMod) {
      this._html2canvasMod = (await import('html2canvas')).default;
    }

    // Suppress resize during capture
    if (this.renderer) {
      this.renderer._resizeSuppressed = true;
    }

    // Protect the player container from external DOM changes.
    // html2canvas appends/removes elements to document.body which can trigger
    // Chromium reflow affecting the player. contain:strict on the player itself
    // makes it immune to any layout changes outside it.
    const prevContain = container.style.contain || '';
    container.style.contain = 'strict';

    try {
      // Draw container background
      const containerRect = container.getBoundingClientRect();
      const containerStyle = getComputedStyle(container);
      const bgColor = containerStyle.backgroundColor;
      if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
        ctx.fillStyle = bgColor;
        ctx.fillRect(containerRect.left, containerRect.top, containerRect.width, containerRect.height);
      }
      const bgImage = containerStyle.backgroundImage;
      if (bgImage && bgImage !== 'none') {
        const urlMatch = bgImage.match(/url\(["']?(.*?)["']?\)/);
        if (urlMatch) {
          try {
            const bgImg = new Image();
            bgImg.crossOrigin = 'anonymous';
            await new Promise<void>((resolve) => {
              bgImg.onload = () => resolve();
              bgImg.onerror = () => resolve();
              setTimeout(() => resolve(), 2000);
              bgImg.src = urlMatch[1];
            });
            if (bgImg.naturalWidth) {
              ctx.drawImage(bgImg, containerRect.left, containerRect.top, containerRect.width, containerRect.height);
            }
          } catch (_) { /* skip failed background */ }
        }
      }

      // Draw each visible widget element
      const elements = container.querySelectorAll('img, video, iframe, canvas');
      let drawn = 0;

      for (const el of elements) {
        const htmlEl = el as HTMLElement;
        if (htmlEl.style.visibility === 'hidden') continue;
        if (htmlEl.style.display === 'none') continue;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) continue;

        try {
          if (el instanceof HTMLImageElement) {
            if (!el.complete || !el.naturalWidth) continue;
            const fit = getComputedStyle(el).objectFit;
            if (fit === 'contain' && el.naturalWidth && el.naturalHeight) {
              const d = this.containedRect(el.naturalWidth, el.naturalHeight, rect);
              ctx.drawImage(el, d.x, d.y, d.w, d.h);
            } else {
              ctx.drawImage(el, rect.left, rect.top, rect.width, rect.height);
            }
            drawn++;
          } else if (el instanceof HTMLVideoElement) {
            if (el.readyState < 2) continue;
            const fit = getComputedStyle(el).objectFit;
            if (fit === 'contain' && el.videoWidth && el.videoHeight) {
              const d = this.containedRect(el.videoWidth, el.videoHeight, rect);
              ctx.drawImage(el, d.x, d.y, d.w, d.h);
            } else {
              ctx.drawImage(el, rect.left, rect.top, rect.width, rect.height);
            }
            drawn++;
          } else if (el instanceof HTMLCanvasElement) {
            ctx.drawImage(el, rect.left, rect.top, rect.width, rect.height);
            drawn++;
          } else if (el instanceof HTMLIFrameElement) {
            const iDoc = el.contentDocument;
            if (!iDoc?.body) continue;

            // Clone iframe DOM into main document for html2canvas rendering.
            // contain: strict prevents layout effects on the live player.
            const captureDiv = document.createElement('div');
            captureDiv.style.cssText = `position:fixed;left:-9999px;top:0;width:${rect.width}px;height:${rect.height}px;overflow:hidden;`;

            // Clone stylesheets with absolute URLs
            const linkPromises: Promise<void>[] = [];
            for (const styleEl of iDoc.querySelectorAll('style')) {
              captureDiv.appendChild(styleEl.cloneNode(true));
            }
            for (const linkEl of iDoc.querySelectorAll('link[rel="stylesheet"]')) {
              const newLink = document.createElement('link');
              newLink.rel = 'stylesheet';
              newLink.href = new URL(linkEl.getAttribute('href') || '', iDoc.baseURI).href;
              captureDiv.appendChild(newLink);
              linkPromises.push(new Promise<void>(resolve => {
                newLink.onload = () => resolve();
                newLink.onerror = () => resolve();
              }));
            }

            // Clone body content with absolute img URLs
            const clonedBody = iDoc.body.cloneNode(true) as HTMLElement;
            for (const img of clonedBody.querySelectorAll('img[src]')) {
              const src = img.getAttribute('src') || '';
              if (src && !src.startsWith('http') && !src.startsWith('data:') && !src.startsWith('blob:')) {
                img.setAttribute('src', new URL(src, iDoc.baseURI).href);
              }
            }
            captureDiv.appendChild(clonedBody);
            document.body.appendChild(captureDiv);

            // Collect natural dimensions from original iframe images
            const origImgs = iDoc.querySelectorAll('img');
            const imgNaturals = new Map<string, { nw: number; nh: number }>();
            origImgs.forEach((img, i) => {
              if (img.naturalWidth && img.naturalHeight) {
                imgNaturals.set(String(i), { nw: img.naturalWidth, nh: img.naturalHeight });
              }
            });

            if (linkPromises.length > 0) {
              await Promise.race([
                Promise.all(linkPromises),
                new Promise(r => setTimeout(r, 500)),
              ]);
            }

            const iframeCanvas = await this._html2canvasMod(captureDiv, {
              useCORS: true, allowTaint: true, logging: false,
              backgroundColor: null,
              width: rect.width, height: rect.height,
              onclone: (clonedDoc: Document) => {
                // Force visible — CSS animations reset to opacity:0 in cloned DOM
                const s = clonedDoc.createElement('style');
                s.textContent = '*, *::before, *::after { animation: none !important; transition: none !important; opacity: 1 !important; }';
                clonedDoc.head.appendChild(s);

                // Fix object-fit: contain sizing for html2canvas
                const clonedImgs = clonedDoc.querySelectorAll('img');
                clonedImgs.forEach((cImg, i) => {
                  const style = clonedDoc.defaultView?.getComputedStyle(cImg);
                  if (!style || style.objectFit !== 'contain') return;
                  const dims = imgNaturals.get(String(i));
                  if (!dims) return;

                  const cW = cImg.clientWidth || parseFloat(style.width) || 0;
                  const cH = cImg.clientHeight || parseFloat(style.height) || 0;
                  if (!cW || !cH) return;

                  const srcAspect = dims.nw / dims.nh;
                  const dstAspect = cW / cH;
                  let drawW: number, drawH: number;
                  if (srcAspect > dstAspect) {
                    drawW = cW; drawH = cW / srcAspect;
                  } else {
                    drawH = cH; drawW = cH * srcAspect;
                  }

                  const wrapper = clonedDoc.createElement('div');
                  wrapper.style.cssText = `width:${cW}px;height:${cH}px;display:flex;align-items:center;justify-content:center;overflow:hidden;`;
                  cImg.style.objectFit = 'fill';
                  cImg.style.width = `${drawW}px`;
                  cImg.style.height = `${drawH}px`;
                  cImg.parentNode?.insertBefore(wrapper, cImg);
                  wrapper.appendChild(cImg);
                });
              },
            });

            document.body.removeChild(captureDiv);
            ctx.drawImage(iframeCanvas, rect.left, rect.top, rect.width, rect.height);

            // Draw videos directly — html2canvas can't render <video> elements.
            // Draw on top of the html2canvas result so video overlays black placeholder.
            const iframeRect = el.getBoundingClientRect();
            for (const vid of iDoc.querySelectorAll('video') as NodeListOf<HTMLVideoElement>) {
              if (vid.readyState < 2) continue;
              const vr = vid.getBoundingClientRect();
              if (vr.width === 0 || vr.height === 0) continue;
              try {
                const fit = iDoc.defaultView?.getComputedStyle(vid)?.objectFit;
                if (fit === 'contain' && vid.videoWidth && vid.videoHeight) {
                  const d = this.containedRect(vid.videoWidth, vid.videoHeight,
                    new DOMRect(iframeRect.left + vr.left, iframeRect.top + vr.top, vr.width, vr.height));
                  ctx.drawImage(vid, d.x, d.y, d.w, d.h);
                } else {
                  ctx.drawImage(vid, iframeRect.left + vr.left, iframeRect.top + vr.top, vr.width, vr.height);
                }
              } catch (_) { /* tainted video */ }
            }

            // Draw canvas elements directly (PDF pages, charts rendered in iframe)
            for (const c of iDoc.querySelectorAll('canvas') as NodeListOf<HTMLCanvasElement>) {
              const cr = c.getBoundingClientRect();
              if (cr.width === 0 || cr.height === 0) continue;
              try {
                ctx.drawImage(c, iframeRect.left + cr.left, iframeRect.top + cr.top, cr.width, cr.height);
              } catch (_) { /* tainted canvas */ }
            }

            drawn++;
          }
        } catch (e: any) {
          log.warn('Screenshot: failed to draw element', el.tagName, e);
        }
      }

      log.debug(`Screenshot: composed ${drawn}/${elements.length} elements`);
      return canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
    } finally {
      container.style.contain = prevContain;
      if (this.renderer) {
        this.renderer._resizeSuppressed = false;
      }
    }
  }

  /**
   * Calculate the destination rect for object-fit: contain.
   * Returns the centered rect that preserves the source aspect ratio
   * within the bounding rect (letterbox/pillarbox).
   */
  private containedRect(
    srcW: number, srcH: number, rect: DOMRect
  ): { x: number; y: number; w: number; h: number } {
    const srcAspect = srcW / srcH;
    const dstAspect = rect.width / rect.height;
    let w: number, h: number;
    if (srcAspect > dstAspect) {
      // Source is wider — fit to width, letterbox top/bottom
      w = rect.width;
      h = rect.width / srcAspect;
    } else {
      // Source is taller — fit to height, pillarbox left/right
      h = rect.height;
      w = rect.height * srcAspect;
    }
    return {
      x: rect.left + (rect.width - w) / 2,
      y: rect.top + (rect.height - h) / 2,
      w, h,
    };
  }

  /**
   * Start periodic screenshot submission
   */
  private startScreenshotInterval() {
    const intervalSecs = this.displaySettings?.getSetting('screenshotInterval') || 0;
    if (!intervalSecs || intervalSecs <= 0) return;

    // Pre-load html2canvas for non-Electron/non-Chromium browsers
    if (!this._html2canvasMod && !(window as any).electronAPI) {
      import('html2canvas').then(m => { this._html2canvasMod = m.default; });
    }

    const intervalMs = intervalSecs * 1000;
    log.info(`Starting periodic screenshots every ${intervalSecs}s`);
    this._screenshotInterval = setInterval(() => {
      this.captureAndSubmitScreenshot();
    }, intervalMs);
  }

  /**
   * Update status message (Platform-specific UI)
   */
  private updateStatus(message: string, type: 'info' | 'error' = 'info') {
    const statusEl = document.getElementById('status');
    if (statusEl) {
      statusEl.textContent = message;
      statusEl.className = `status status-${type}`;
    }
    if (type === 'error') {
      log.error('Status:', message);
    } else {
      log.info('Status:', message);
    }
  }

  private showOfflineIndicator() {
    this.timelineOverlay?.setOffline(true);
  }

  private removeOfflineIndicator() {
    this.timelineOverlay?.setOffline(false);
  }

  /**
   * Check if the sync lead is alive (for follower delegation).
   * Returns true if any peer with role 'lead' has been seen in the last 15s.
   */
  private _syncLeadAlive(): boolean {
    if (!this.syncManager) return false;
    for (const [, peer] of this.syncManager.followers) {
      if (peer.role === 'lead' && Date.now() - peer.lastSeen < 15000) {
        return true;
      }
    }
    return false;
  }

  /**
   * Cleanup
   */
  cleanup() {
    this.core?.cleanup();
    this.renderer?.cleanup();

    if (this._screenshotInterval) {
      clearInterval(this._screenshotInterval);
      this._screenshotInterval = null;
    }

    if (this._wakeLock) {
      this._wakeLock.release();
      this._wakeLock = null;
    }

    if (this.downloadOverlay) {
      this.downloadOverlay.destroy();
    }

    if (this.timelineOverlay) {
      this.timelineOverlay.destroy();
    }

    // Disconnect iframe observer
    if (this._iframeObserver) {
      this._iframeObserver.disconnect();
      this._iframeObserver = null;
    }

    // Tear down xp:datasource shared cache + bridge
    if (this._datasourceBridge) {
      this._datasourceBridge.destroy();
      this._datasourceBridge = null;
    }
    if (this.datasourceClient) {
      this.datasourceClient.stop();
      this.datasourceClient = null;
    }

    // Remove SW message listeners
    if (navigator.serviceWorker) {
      if (this._swIcHandler) {
        navigator.serviceWorker.removeEventListener('message', this._swIcHandler);
        this._swIcHandler = null;
      }
    }

    // Clean up DownloadManager
    downloadManager?.clear();

    if (this._probeTimer) {
      clearTimeout(this._probeTimer);
      this._probeTimer = null;
    }

    if (this._mediaStatusTimer) {
      clearTimeout(this._mediaStatusTimer);
      this._mediaStatusTimer = null;
    }
  }
}

function startPlayer() {
  const player = new PwaPlayer();
  player.init().catch(error => {
    log.error('Failed to initialize:', error);
  });
  window.addEventListener('beforeunload', () => {
    player.cleanup();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startPlayer);
} else {
  startPlayer();
}
